// ============================================================================
// MOSTRA BASE — Motore riutilizzabile per mostre virtuali 3D (Shopify)
// ----------------------------------------------------------------------------
// Questo è il "motore" spogliato di ogni contenuto a tema: una stanza bianca
// vuota con tutti i sistemi funzionanti già collaudati, pronta da usare come
// punto di partenza per una nuova mostra senza dover ricostruire da zero
// l'ingresso/iscrizione, il movimento, il pannello prodotto, ecc.
//
// Cosa C'È:
//  1. Overlay d'ingresso con iscrizione email → Shopify (form nativo +
//     redirect automatico via return_to, niente iframe/fetch fragili)
//  2. Audio engine sintetizzato (Web Audio API) con suoni posizionali 3D
//  3. Stanza bianca minimale: pavimento, 4 pareti, soffitto, luce statica
//  4. Cornici-prodotto sui muri (si popolano da solo dall'array `l` più
//     sotto, distribuite automaticamente su 3 pareti — la quarta è
//     riservata al cartello di uscita)
//  5. Cartello di uscita interattivo
//  6. Controlli: drag-to-look, joystick virtuale (mobile), tastiera WASD,
//     raycasting per aprire il pannello di un'opera o confermare l'uscita
//  7. Pannello prodotto con "aggiungi al carrello" diretto (Shopify AJAX API)
//  8. Hook generici per estendere: `ot` (callback eseguite ad ogni frame,
//     utile per animazioni personalizzate) e `pt` (posizioni da far evitare
//     al visitatore, utile per oggetti fissi che aggiungerai nella stanza)
//
// Cosa NON C'È (va aggiunto per ogni mostra specifica):
//  - Qualsiasi elemento decorativo/narrativo (fiori, robot, droni, ecc.)
//  - Ciclo giorno/notte o illuminazione animata (qui è statica)
//  - Texture procedurali a tema — i materiali sono tinte piatte neutre
//
// Come iniziare una nuova mostra da questo file:
//  1. Rinomina il file e aggiorna il riferimento nel blocco Liquid
//  2. Compila l'array `l` con le tue opere (schema commentato più sotto)
//  3. Personalizza `c` (URL di uscita) e i colori dei materiali della stanza
//  4. Aggiungi i tuoi elementi 3D con `v.add(...)`, e se vuoi che si animino
//     ad ogni frame registra un callback in `ot.push({ update: (t, dt) => {...} })`
// ----------------------------------------------------------------------------
// Revisione 11/10/2026 (Stanza 02 — Anatomia del Margine):
//  - joystick nascosto finché non si entra e quando pannello, libro o
//    conferma di uscita sono aperti (prima stava sopra a tutto)
//  - prezzi, varianti, foto e disponibilità letti live da Shopify
//    (window.ROOM02_PRODUCTS, generato in Liquid nello snippet); l'array
//    `l` resta come fallback
//  - "Add to cart" aggiunge davvero al carrello (variantId compilati)
//  - foto dell'opera nel pannello, link al carrello dopo l'aggiunta
//  - iscrizione email con invio nativo + return_to (come Stanza 01, niente
//    iframe nascosto); chi si è già iscritto entra direttamente
//  - interfaccia tutta in inglese; messaggi d'errore leggibili e chiudibili
//  - su touch: niente profondità di campo, pixel ratio 1.5 e texture dei
//    palazzi a risoluzione ridotta (memoria GPU molto più bassa)
// Revisione 12/10/2026 — resa cinematografica:
//  - tone mapping ACES Filmic: alte luci morbide, ombre più profonde,
//    colori meno "videogioco" (esposizione regolabile in CINE.exposure)
//  - opere, cornici e insegne escluse dal tone mapping: i colori delle
//    opere restano quelli originali
//  - sguardo con inerzia (CINE.lookSmoothing): la camera segue il dito o
//    il mouse con un leggero ritardo, come una ripresa a mano
//  - respiro della camera: oscillazione verticale di ~1 cm da fermi
//  - libro sulla scrivania: si illumina di una luce calda quando il
//    cursore ci passa sopra (solo mouse; su touch non esiste l'hover)
// Revisione 12/10/2026 (2):
//  - email ricordata già al click su "Enter": si entra direttamente anche
//    se Shopify non torna con ?entered=1 (captcha, email già iscritta)
//  - sguardo al tocco invertito, stile Street View (mouse invariato)
// ============================================================================
!(function () {
  // Alcuni temi Shopify applicano un transform CSS (es. animazioni di
  // comparsa allo scroll) alla sezione che contiene questo blocco Custom
  // Liquid. Un transform su un antenato rompe silenziosamente il
  // position:fixed dei suoi discendenti (diventa relativo all'antenato
  // invece che alla viewport). Spostando gli overlay come figli diretti
  // di <body> li rendiamo immuni a questo problema, qualunque sia il
  // tema o la sezione in cui viene inserito il blocco.
  [
    "err-banner",
    "entry",
    "hud",
    "move-hint",
    "joystick-zone",
    "panel",
    "book-panel",
    "exit-confirm",
  ].forEach((id) => {
    const el = document.getElementById(id);
    el && el.parentElement !== document.body && document.body.appendChild(el);
  });
  // Mostra/nasconde il joystick. Resta nascosto finché il visitatore non
  // entra e ogni volta che è aperto il pannello di un'opera, il libro o la
  // conferma di uscita, così non si sovrappone mai ai loro pulsanti.
  function setJoystick(on) {
    const js = document.getElementById("joystick-zone");
    js && js.style.setProperty("display", on ? "block" : "none", "important");
  }
  // Stili del joystick impostati via JS con !important, più forti di
  // qualunque foglio di stile esterno del tema. z-index 25: sopra la scena
  // e l'HUD (20), sotto pannello (30), libro (35), uscita (40), ingresso (50).
  (function () {
    const js = document.getElementById("joystick-zone");
    if (!js) return;
    const force = (el, rules) => {
      if (!el) return;
      for (const prop in rules)
        el.style.setProperty(prop, rules[prop], "important");
    };
    force(js, {
      position: "fixed",
      left: "max(20px, env(safe-area-inset-left))",
      bottom: "max(20px, env(safe-area-inset-bottom))",
      width: "120px",
      height: "120px",
      "z-index": "25",
      display: "none",
      visibility: "visible",
      opacity: "1",
      "pointer-events": "auto",
    });
    force(document.getElementById("joystick-base"), {
      position: "absolute",
      inset: "0",
      "border-radius": "50%",
      background: "rgba(255,255,255,0.32)",
      border: "3px solid rgba(255,255,255,0.9)",
      "box-shadow":
        "0 2px 12px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(0,0,0,0.25)",
      display: "block",
      visibility: "visible",
      opacity: "1",
    });
    force(document.getElementById("joystick-knob"), {
      position: "absolute",
      left: "50%",
      top: "50%",
      width: "54px",
      height: "54px",
      margin: "-27px 0 0 -27px",
      "border-radius": "50%",
      background: "rgba(17,17,17,0.9)",
      border: "2px solid rgba(255,255,255,0.95)",
      "box-shadow": "0 2px 8px rgba(0,0,0,0.4)",
      display: "block",
      visibility: "visible",
      opacity: "1",
    });
  })();

  // --- Configurazione ---------------------------------------------------
  // Stanza rettangolare chiusa, stretta e lunga: il visitatore entra e si
  // trova davanti a una lunga distanza vuota fino alla finestra sulla
  // parete opposta. Soffitto alto per accentuare il senso di vuoto.
  const t = { width: 14, depth: 20, height: 4.6 }, // dimensioni della stanza
    e = {
      eyeHeight: 1.68,
      moveSpeed: 2.5, // passo volutamente lento, non è una mostra da attraversare in fretta
      wallMargin: 0.6,
      spawn: { x: 0, z: 7 }, // il visitatore entra con la finestra già davanti a sé
      lookSensitivity: 0.0028,
    },
    n = { ambientIntensity: 0.46, lightIntensity: 1.15 }, // visibile, ma resta penombra
    o = { masterVolume: 0.22 },
    c = "https://stenjs.com/collections/all", // URL della pagina di uscita
    l = [
      {
        title: "Gelato cieco",
        meta: "Anatomia del Margine — Fine Art su carta opaca 300g, A3, ed. limitata 30 copie",
        price: "€40,00",
        handle: "gelato-cieco",
        url: "https://stenjs.com/products/gelato-cieco",
        variantId: 54239011701065,
        imageUrl:
          "https://stenjs.com/cdn/shop/files/Gelato_Cieco.png?v=1776764252",
        windowName: "AdM — 01",
        color: 1710618,
      },
      {
        title: "Ikiru",
        meta: "Anatomia del Margine — Fine Art su carta opaca 300g, A3",
        price: "€40,00",
        handle: "ikiru",
        url: "https://stenjs.com/products/ikiru",
        variantId: 54239048630601,
        imageUrl:
          "https://stenjs.com/cdn/shop/files/Opera_senza_titolo18.png?v=1776763947",
        windowName: "AdM — 02",
        color: 1710618,
      },
      {
        title: "Lecca lecca rosso",
        meta: "Anatomia del Margine — Fine Art su carta opaca 300g, A3, ed. limitata 30 copie",
        price: "€40,00",
        handle: "lecca-lecca-rosso",
        url: "https://stenjs.com/products/lecca-lecca-rosso",
        variantId: 54238452089161,
        imageUrl:
          "https://stenjs.com/cdn/shop/files/Lecca_Lecca_Rosso.png?v=1776764225",
        windowName: "AdM — 03",
        color: 1710618,
      },
    ],
    // Testi del libro sulla scrivania: uno per ogni opera alle pareti,
    // stesso ordine e stessi titoli.
    libro = [
      {
        title: "Lecca lecca rosso",
        text: `Mi sono alzato saranno state le tre, forse le quattro. Febbraio. Freddo umido. Fredda umida la maglia sudata. Fuori una nebbia che appiattisce tutto, come se il mondo avesse deciso di smettere di esistere per qualche ora.

L'appartamento immobile nel suo ordine finto. Tutto sistemato, ma solo in superficie. Un disastro contenuto, educato, che aspetta la luce giusta per mostrarsi.

Sono andato in bagno. Cagata alcolica, di quelle che ti fanno l'inventario della serata senza chiederti il permesso.

Due Old Fashioned.
La ciliegina è sempre la prima cosa che guardo.

Piccola. Rosso finto. Lucida. Immersa nell'alcol come se fosse lì per scelta. La mordi e non sa di frutta, sa di zucchero industriale e nostalgia. È decorazione. Non è necessaria. Sta lì per essere vista, non per essere capita. Ogni volta che la prendo penso la stessa cosa: è una promessa che non mantiene nulla. Una bugia elegante.

Due sono abbastanza forti da alleggerire la testa senza spegnerla del tutto. Poi c'è stato il mezcal. Quello che non ti accompagna: ti prende per la nuca. Ti fa girare come una trottola per dieci minuti buoni, giusto il tempo di farti credere che sei ancora in controllo. Fa il suo lavoro. Senza poesia.

Seduto sul cesso, col freddo delle piastrelle che sale, capisci tutto. Cosa hai bevuto. In che ordine. E perché.

Quando esco dal bagno vado in cucina. È buia. Resto un attimo fermo, come se stessi aspettando che qualcosa mi venga incontro.

Alzo appena la tapparella. Non del tutto. Solo un filo. Entra l'inquinamento luminoso. Bianco sporco. Una luce che non illumina davvero, ma nemmeno lascia buio. La città che filtra dentro.

Sul ripiano grigio cemento ci sono le Marlboro. Gold, ma il pacchetto è bianco sporco. Le ho lasciate lì apposta. Ho smesso di fumare in casa, almeno ufficialmente. Ma la moka bruciata che uso come posacenere le ricorda. Odore vecchio, marcio, che si attacca agli angoli. Come certe abitudini che pensi di aver lasciato, ma continuano a vivere nei muri.

Afferro il pacchetto al buio, a memoria, come se sapessi sempre dove sono le cose che mi fanno male.

Mi muovo piano verso il soggiorno. Attraverso l'ingresso come se stessi passando dentro parti diverse del mio cervello. Quando arrivo accendo la lampada da lettura sul tavolino.

L'appartamento stava lì, immobile, come se respirasse a fatica. L'ordine era perfetto a prima vista: libri allineati, divano grigio senza macchie evidenti, bicchieri lucidi. Ma era un ordine finto, fragile.
Bastava guardare un attimo in più, inclinare la testa, e il disastro cominciava a mostrarsi.

Sul pavimento, sotto il tavolo, un tovagliolo appena nascosto. La tazza di caffè del mattino prima non lavata. La coperta accartocciata sul divano, gialla, impastata del profumo di una sconosciuta. Piccoli indizi, insignificanti, che gridavano il vero caos. Gli odori raccontavano meglio di qualsiasi arredamento: candela ai frutti rossi che cercava di coprire lo stantio dell'aria, della pelle, dell'alcol del giorno passato. I vestiti da palestra ammucchiati all'ingresso. Un filo di polvere sui bordi dei quadri, non abbastanza spesso da essere percepito subito, ma sufficiente per far capire che nessuno li puliva davvero.

E l'ho visto.

Sopra il mobile del soggiorno c'era la bottiglia di assenzio a forma di teschio. Una sopravvissuta maranza. Era scampata a una festa di quindicenni che credevano di essere già troppo vecchi. La festa era degenerata come degenerano sempre: risate isteriche, bicchieri rotti, qualcuno che vomita, qualcun altro che parla di morte come se fosse un'idea originale. Il tappo si era spaccato. Ne era rimasto solo un pezzo che rientrava.

Negli anni io quel tappo l'ho scavato. Piano. Con pazienza. Sapevo che quell'assenzio non l'avrei mai bevuto. Non era per quello. L'ho sigillato con della cera. Verde dentro, morto fuori. Un'essenza mummificata.

Sopra, però, restava il buco.

E lì ci infilavo un clipper.
Perfetto.
Me lo aveva regalato Chiodino, un memento mori. Accendino, tappabottiglie, tappo improvvisato. Comodo da avere in soggiorno. Una funzione chiara.
Un senso.

Poi, a un certo punto, ho tolto il clipper. E ci ho messo il lecca lecca rosso.

Quella notte era lì. Rosso chimico. Cuore. Plastica tesa. Stecco bianco infilato esattamente nel buco della bottiglia, come se fosse sempre stato quello il suo posto. Un innesto sbagliato, ma preciso.

Mi sono fermato a guardarlo. Mezzo addormentato. Gli occhi gonfi.

Ho preso una Marlboro. Ho tirato su la tapparella del balcone. E sono uscito.

Il freddo mi ha schiaffeggiato in faccia. La nebbia mi entrava nei polmoni insieme al fumo. Stavo lì, infreddolito, in piedi storto, a fissare la strada sotto casa.

Durante tutta la sigaretta è passata una sola macchina.

Una sola.

Rossa. Lenta. Senza fretta. Senza musica. Ha attraversato la strada come se fosse l'unica cosa autorizzata a muoversi in quella notte. Mi ha ricordato il locale, o la ciliegina. Stessa logica. Stesso passo.
Non arriva. Non va via.
Passa.

Nient'altro. Nessun rumore. Nessuna vita.

Sono rientrato. Ho chiuso la porta finestra. E proprio mentre la chiudevo ho visto una scintilla sulla carta trasparente del lecca lecca. Un punto minuscolo, forse niente. Forse solo l'occhio stanco. Ma c'era.

Sono tornato in camera. Mi sono buttato sul letto. Ho dormito ancora un paio d'ore. Giusto il tempo di svegliarmi dieci minuti prima della sveglia, come se qualcuno avesse acceso la luce dentro.

Era ancora lì.
Rosso. Incartato.
Al posto dell'accendino.

Me l'avevano dato al locale.

Il locale aveva uno di quei nomi pseudo-pop. Una parola breve, rotonda, inglese quanto basta per sembrare internazionale. Dentro era esattamente così. All'inizio bar, poi qualcos'altro. Le luci scendono di mezzo tono, la musica sale di mezzo grado, e improvvisamente la gente è più vicina. Abbastanza rumoroso da non parlare. Abbastanza buio da non guardare. Abbastanza pieno da non sentire niente. Ci vai quando non vuoi uscire ma devi farlo.

C'era una mia amica dietro il bancone quella sera. O meglio, un'amica del gruppo in cui ero entrato. Non so se lei mi vede come un amico, credo più un conoscente. Ci aveva regalato qualche lecca lecca, presi da dietro il bancone come si regalano le cose che non costano niente ma fanno scena.

Io li ho distribuiti. E uno me lo son tenuto. Uno l'ho dato a lei. Non abbastanza giovani da essere giovani, non abbastanza vecchi da essere saggi.

Quella sera c'era anche lei. Io sapevo che era fidanzata. Lo sapevo bene. E anche lei ha ricevuto il lecca lecca. Ma lei l'ha mangiato subito.

Io l'ho conservato.

Era diventato una reliquia. Come i bigliettini dei Baci Perugina che mi ha regalato negli anni. Frasi scritte da altri, pensate per nessuno in particolare, ma che io leggevo come fossero state lasciate lì apposta per me. Piccoli oggetti inutili, eppure sempre presenti.

Quelle frasi erano la carta che incartava il lecca lecca rosso.
La prima volta che l'avevo vista, qualcosa si era acceso senza chiedere permesso.
Un colpo secco. Una convinzione immediata: è lei.
Col tempo era nato un rapporto strano. Bello, sì. Fatto di messaggi giusti, silenzi calibrati, confidenze a metà. Senza mai essere una scelta.

Ieri sera, dopo due anni, a metà del secondo Old Fashioned, mi sono scartato.

La prima ciliegia era già finita da un pezzo, la seconda stava lì, lucida e molle, un po' colata di zucchero, a mezza via tra un frutto e un ricordo. La mordo, e il liquido dolce mi cola sul pollice e sull'indice, appiccicandosi. L'alcol ti prende, lento, pesante, ti riempie lo stomaco e lo spinge giù verso la testa. Ti gira tutto ma non vuoi fermarla. Non ancora.

Il locale era sopraelevato. Una pedana sul mondo. Tu guardi la strada sotto, la gente che passa, e ti senti osservato senza essere visto. Lei lo odiava. Lo sapevo. Luci soffuse che vogliono dare intimità ma tradiscono tutto: il pavimento, il legno scuro, le bottiglie dietro il bancone. Fuori l'aria fumatori ha un odore di vecchio legno, di fumo che non se ne va mai del tutto, di alcol e candela che cerca di coprire qualcosa di più sporco, più vero.

Le parole sono uscite come scarti di zucchero da un sacco strappato. Appiccicose, lente, pesanti. «Ti volevo dire…» e non ho potuto finire la frase senza sentire il liquido dolce e forte che mi riempiva la bocca. La mia lingua sapeva di bourbon e zucchero, il palato assaporava il mezcal che ci sarebbe stato poi.

Confessare era come rompere un lecca lecca rosso. La plastica secca scricchiola sotto i denti, il gusto chimico arriva in bocca e resta, attacca il palato, le gengive. Ti sporca. Ti pesa. Ti segue anche quando chiudi la bocca.

Lei mi guarda. Non sorride. Non ridacchia. Non fa finta di niente. Solo respira. E io sento il silenzio, spesso, viscoso, che si attacca alla pelle come una nebbia densa. La musica di sottofondo non è mai abbastanza alta per non sentire i bicchieri degli altri vibrare sui tavoli.

La stanchezza. Dio, la stanchezza.
Ogni parola che esce pesa di più del bicchiere che tengo tra le mani e che svuoto per provare ad alleggerire le parole. Non c'è dramma. Solo fisico, reale, sporco.

Lei ascolta. Il suo silenzio è una nebbia più densa di quella fuori. Non mi salva, non mi distrugge, non mi offre tregua. Solo questo sapore chimico, dolce, appiccicoso che rimane sulle mani, sulle labbra.

Poi parla.
E quando le parole finiscono, quando il bicchiere è vuoto e il locale è ancora lì, sopraelevato, luci giallo sporco, musica che non è mai abbastanza, mi rendo conto che non posso più far finta di niente. Non ci sono scorciatoie. Non ci sono magie.

Così stamattina mi sono avvicinato alla bottiglia.

Il soggiorno era silenzioso.
Freddo.
L'assenzio dentro la bottiglia era fermo, verde come se dormisse dopo una sbronza. Come se avesse aspettato anche lui.
Ho guardato il lecca lecca rosso. Il cuore di plastica, teso, lucido. Il cellophane era diventato opaco, quasi bianco agli angoli, come se avesse respirato troppo a lungo. Il bastoncino era leggermente piegato forse il calore del termosifone, forse il peso di quello che ci avevo messo sopra.

L'ho preso. Non con rabbia, non con gesto teatrale.

L'ho tenuto tra le dita, sentendo la plastica secca scricchiolare sottile, e l'ho lasciato cadere.

Il rosso ha toccato il mobile e ha fatto un piccolo rumore secco.

E per la prima volta, sopra e dentro quella bottiglia, non c'era più niente.

Solo il vuoto.

Sporco, freddo, reale.`,
      },
      {
        title: "Gelato cieco",
        text: `Mio nonno non era mio nonno.
Era il compagno di mia nonna. Arrivato dopo la morte del vero nonno, come arrivano certi uomini: senza fare rumore, senza reclamare posto.

Aveva un naso enorme. Le orecchie larghe, quasi sproporzionate. E due occhi azzurro ghiaccio.
Uno era cieco.

Io me ne dimenticavo sempre. Perché erano identici. Stesso colore duro, stesso taglio netto. Solo che uno non vedeva niente. La pupilla restava un filo più immobile. Ma lui aveva imparato a muoversi come se vedesse tutto. Ruotava leggermente la testa. Si posizionava di lato. Copriva il vuoto.

Era stato il fratello ad accecarlo.
Durante una battuta di caccia.
Un colpo partito male.
Così dicevano.

Era un uomo mite. Non nel senso debole. Nel senso lento. Parlava poco, ascoltava tutto. Non alzava la voce nemmeno quando avrebbe avuto diritto di farlo. Aveva una fede semplice, concreta.
Niente filosofia.
Segno della croce, lavoro, silenzio.

Veniva dalle campagne del Veneto.
Figlio di contadini. Terra sotto le unghie prima ancora di imparare a scrivere bene il suo nome. Stesse abitudini, stessi orari, stessi percorsi. Abitudinario fino a diventare prevedibile. Quasi noioso.

Negli anni Cinquanta in Italia le cose si sistemavano senza fare troppo rumore.
Il fratello era già sposato. Lei era l'amante. Rimase incinta. E a un certo punto la soluzione fu lui. La prese con sé. La sposò. Crebbe un figlio che non era suo come se lo fosse.

Non ho mai visto rabbia nei suoi occhi. Forse l'aveva consumata tutta il giorno del colpo di fucile. Forse non l'aveva mai avuta.

Quando ero piccolo mi faceva raddrizzare i chiodi storti e se mi colpivo il pollice mi diceva: «Son cose che capitano ai vivi».
Versava lo spirito sul dito.

Da bambino vedevo solo un uomo paziente che mi insegnava a stringere bene il manubrio e a non mollare i pedali in salita. Andavamo nelle campagne dietro Busto. Strade dritte, fossi lenti, odore di terra bagnata e concime. Lui pedalava regolare. Io zigzagavo.

Un giorno disse: «Oggi devo andare al cimitero.»

Tagliammo dai campi e arrivammo davanti al Monumentale di Busto. Lasciammo le bici contro il muro. Senza lucchetti.
«Dio vede e provvede.»

Era luglio. Caldo pieno, ma ancora sopportabile. L'aria vibrava sopra la ghiaia.

Entrammo.

Spazio largo. Ghiaia che scricchiolava sotto le scarpe. Muri grigi, spessi, brutalisti. Cemento che non consola. Zone d'ombra nette, lame di luce bianca. Un posto aperto ma pieno di tagli nello sguardo.

Lui teneva i fiori in mano. Non guardava in giro. Camminava come se conoscesse ogni angolo cieco.

Arrivati alla tomba si tolse il cappello. Lo strinse tra le mani grosse. Abassò la testa.

Pregava per sua moglie.
La donna che aveva amato per tutta la vita.
La donna che aveva amato suo fratello.
La donna che gli aveva dato un figlio che non era suo.

Io mi allontanai di qualche metro. Fingevo di leggere nomi. Ogni tanto lo guardavo di lato.

Il suo occhio buono seguiva la lapide. Quello non buono fissava il vuoto. Non si chiudeva mai del tutto.
Restava lì, aperto, lucido, inutile.
Da vicino si vedeva che non metteva a fuoco. Era come una finestra con il vetro pulito ma la stanza dietro vuota.

Mi sono chiesto spesso se quel colpo fosse stato davvero un errore. Un incidente di caccia. Bosco, fucili, adrenalina. Oppure uno di quei colpi che partono quando qualcosa dentro ha già deciso.

Non l'ho mai saputo. Lui non ne parlava.

Al cimitero camminava leggermente inclinato. Si posizionava sempre in modo da avere il mondo sul lato buono. Compensava.

Il cimitero era uno spazio enorme, ma bastava fare due passi per perdere la visuale. Muri spessi che tagliavano l'orizzonte. Angoli improvvisi. Zone d'ombra dove la luce non arrivava mai davvero. Un luogo costruito per ricordarti che non puoi vedere tutto.

Io non so se sarei capace di camminare così. Con metà campo visivo perso. Sapendo che chi ti ha tolto la vista è lo stesso che ti ha tolto la donna.

Io credo che girerei la testa dall'altra parte. O sparerei per primo.

Lui no.

Lui ha imparato a stare dentro l'angolo morto. A convivere con una parte che non vede. A compensare. A non chiedere spiegazioni.

Uscimmo. Rimise il cappello. Mi guardò.
«Vuoi un gelato?»

Pedalammo fino al bar vicino. Seduti su una panchina all'ombra, la strada davanti. Il mio gelato colava sulle dita. Il suo si scioglieva lento, senza fretta.
Non parlava mai di suo fratello.
Non parlava mai di sua moglie.
Non parlava mai del colpo di fucile.

Aveva perso un occhio. Aveva perso una donna. Aveva accettato un figlio non suo. Aveva accettato me.

Io non so se ce l'avrei fatta.

Un letto dove qualcuno ha già dormito prima di te.
Uno sguardo che non ti appartiene del tutto.

Quando penso a lui davanti a quella tomba, dove ora è seppellito, penso alla quantità di cose che non ha chiesto.
Non ha chiesto spiegazioni.
Non ha chiesto giustizia.
Non ha chiesto vendetta.
Ha chiesto solo di restare.

Io ho paura di non essere così. Ho paura di rovinare tutto per non farmi rovinare. Ho paura di non saper perdonare. O peggio, di non saper sopportare.

Lui era serafico. Ma non era stupido.
Sapeva.

Eppure andava al cimitero con i fiori. Ogni tanto. Senza scena.

Io quando amo, voglio essere visto intero. Non sopporterei metà verità. Non sopporterei metà amore.

Lui invece ha fatto pace con la metà.

Forse la forza non è distruggere.
Forse è restare.

Gli volevo bene.`,
      },
      {
        title: "Ikiru",
        text: `L'altalena è il primo posto dove ho imparato a stare fermo, muovendomi.
Avanti.
Indietro.
Avanti.
Indietro.
Promette infinito e ti riporta sempre allo stesso punto.

Da piccolo era libertà pura. Le mani di mio nonno dietro la schiena. Quelle di mia nonna che spingevano piano, come se avessero paura di rompermi. Il parco vicino casa loro. Di giorno bambini e merendine.
Di sera altro.
Spade nell'erba. Il pallone bucato grazie a qualche fenomeno della notte prima.

Ma quando stavo seduto su quell'asse sospeso non esisteva niente.
Solo aria.

Poi c'è stato mio padre.
Divorziati. Case cambiate. Una volta si trasferì a Bergamo per un annetto. Attico dall'azienda. Ultimo piano. Balcone alto abbastanza da farti capire quanto sei piccolo.
Sul terrazzo mise un'altalena.

Mi spingeva forte.
Più forte.
Più forte.

Il momento migliore non era quando salivo.
Era quando il corpo non apparteneva più alla gravità.
Un secondo di sospensione.
Un secondo in cui potevo scegliere.

Saltavo.

Vedevo di lato il vuoto. I metri. Il cemento. L'asfalto.
Non saltavo per scappare.
Saltavo per sentire che potevo cadere.

Il vento entrava da sotto la maglia.
Le suole toccavano terra.
Le ginocchia assorbivano.
E già stavo tornando sull'altalena.

Quando resti solo, devi imparare a spingerti con le gambe.
E non è la stessa cosa.

Mio padre mi guardava dal terrazzo.
Non applaudiva.
Non commentava.
Aspettava che salissi di nuovo.
Come se il punto non fosse il salto.
Come se il punto fosse tornare.

Anni dopo ho dipinto quell'altalena. Un uomo radioattivo seduto lì sopra. Il poster di Ikiru di Akira Kurosawa.
A un certo punto è su un'altalena, nella neve. Non è felice. Non è triste.
È lucido.

Essere dentro il meccanismo e non lasciare nessuna traccia.

Ho cercato di capire per anni cosa volesse dire quella scena.
Un uomo che sa di morire, seduto su un'altalena, sotto la neve.
Non sta aspettando niente.
Non sta rimpiangendo niente.
Sta solo lì.
Dentro il movimento.
Dentro il freddo.
Completamente presente in una cosa che non serve a niente.

Forse è questo.
Forse è solo questo.

Compilavo i moduli del piano di studi con un mouse che non usavo mai per altro. Solo per quello. Lo rimettevo nel cassetto dopo. Non so perché.

Il primo tatuaggio che ho fatto è stato quell'uomo sull'altalena. Sul costato. Vicino al cuore.

L'inchiostro è permanente.
Anche se in realtà sbiadisce.
Cambia con la pelle.
Si deforma con gli anni.
Non è una traccia.
È una pressione che rimane.

Quel quadro l'ho regalato a mia madre.

Rimane sospeso.`,
      },
    ],
    d = document.getElementById("email"),
    h = document.getElementById("enter-btn");
  // Dati live da Shopify (generati in Liquid nello snippet della stanza):
  // se cambi prezzo, foto o disponibilità di un prodotto in admin, la
  // stanza si aggiorna da sola. Se un prodotto manca, resta il fallback.
  (function () {
    const live = window.ROOM02_PRODUCTS || {};
    l.forEach((p) => {
      const d = live[p.handle];
      if (!d) return;
      ["variantId", "price", "imageUrl", "url", "available"].forEach((k) => {
        null != d[k] && "" !== d[k] && (p[k] = d[k]);
      });
    });
  })();
  const ENTRY_KEY = "utopia_room02_entered",
    isTouch = !!(window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
  let E = null,
    p = null,
    m = null,
    u = !1;

  // --- Audio engine sintetizzato (Web Audio API), con suoni posizionali 3D ---
  function M(t) {
    if (!t || !E.createPanner) return null;
    const e = E.createPanner();
    return (
      (e.panningModel = "HRTF"),
      (e.distanceModel = "inverse"),
      (e.refDistance = 3),
      (e.maxDistance = 60),
      (e.rolloffFactor = 1.1),
      e.positionX
        ? ((e.positionX.value = t.x),
          (e.positionY.value = t.y),
          (e.positionZ.value = t.z))
        : e.setPosition && e.setPosition(t.x, t.y, t.z),
      e
    );
  }
  function f(t, e, n, a, o, i) {
    if (!E) return;
    const s = E.currentTime,
      r = E.createOscillator();
    ((r.type = a || "sine"),
      r.frequency.setValueAtTime(t, s),
      e && r.frequency.exponentialRampToValueAtTime(Math.max(1, e), s + n));
    const c = E.createGain();
    (c.gain.setValueAtTime(1e-4, s),
      c.gain.linearRampToValueAtTime(o || 0.1, s + 0.01),
      c.gain.exponentialRampToValueAtTime(1e-4, s + n));
    const l = M(i);
    (r.connect(c),
      l ? (c.connect(l), l.connect(p)) : c.connect(p),
      r.start(s),
      r.stop(s + n + 0.05));
  }
  function w(t, e, n, a) {
    if (!E) return;
    const o = E.currentTime,
      i = Math.max(1, Math.floor(E.sampleRate * t)),
      s = E.createBuffer(1, i, E.sampleRate),
      r = s.getChannelData(0);
    for (let t = 0; t < i; t++) r[t] = (2 * Math.random() - 1) * (1 - t / i);
    const c = E.createBufferSource();
    c.buffer = s;
    const l = E.createBiquadFilter();
    ((l.type = "lowpass"), (l.frequency.value = n || 1800));
    const d = E.createGain();
    d.gain.value = e || 0.06;
    const h = M(a);
    (c.connect(l),
      l.connect(d),
      h ? (d.connect(h), h.connect(p)) : d.connect(p),
      c.start(o));
  }
  function g() {
    f(600, 420, 0.06, "square", 0.045);
  }
  let T = !1;
  // Banner messaggi per il visitatore: si chiude con un tocco e, salvo
  // errori bloccanti (persist = true), sparisce da solo dopo 7 secondi.
  let errTimer = null;
  function y(t, persist) {
    const e = document.getElementById("err-banner");
    ((e.textContent = t), (e.style.display = "block"), clearTimeout(errTimer));
    persist ||
      (errTimer = setTimeout(() => {
        e.style.display = "none";
      }, 7e3));
  }
  document.getElementById("err-banner").addEventListener("click", function () {
    this.style.display = "none";
  });
  // Nasconde l'overlay d'ingresso, avvia l'audio e mostra il suggerimento
  // di movimento. Usata sia dopo un click valido su "Enter the exhibit"
  // (come fallback se il modulo non è disponibile) sia automaticamente al
  // ritorno da Shopify dopo l'iscrizione (vedi il controllo su ?entered=1
  // più sotto), così non serve un secondo click per entrare.
  function dismissEntry() {
    (function () {
      if (!E)
        try {
          ((E = new (window.AudioContext || window.webkitAudioContext)()),
            (p = E.createGain()),
            (p.gain.value = u ? 0 : o.masterVolume),
            p.connect(E.destination),
            (m = E.createGain()),
            (m.gain.value = 0.045),
            m.connect(p));
          const t = E.createOscillator();
          ((t.type = "sine"), (t.frequency.value = 41), t.connect(m));
          const e = E.createOscillator();
          ((e.type = "sine"), (e.frequency.value = 82));
          const n = E.createGain();
          ((n.gain.value = 0.3), e.connect(n), n.connect(m));
          const a = E.createOscillator();
          ((a.type = "sine"), (a.frequency.value = 0.035));
          const i = E.createGain();
          ((i.gain.value = 0.014),
            a.connect(i),
            i.connect(m.gain),
            t.start(),
            e.start(),
            a.start());
        } catch (t) {
          console.warn("Audio non disponibile:", t);
        }
      // Senza un gesto dell'utente (ingresso automatico) il browser tiene
      // l'audio sospeso: lo riattiviamo al primo tocco/click.
      if (E && "suspended" === E.state) {
        const resume = () => {
          (E.resume(), document.removeEventListener("pointerdown", resume, !0));
        };
        document.addEventListener("pointerdown", resume, !0);
      }
    })();
    g();
    const e = document.getElementById("entry");
    (e.classList.add("hidden"),
      setTimeout(() => {
        e.style.display = "none";
      }, 650),
      (T = !0),
      setJoystick(!0),
      document.getElementById("move-hint").classList.add("show"),
      setTimeout(
        () => document.getElementById("move-hint").classList.remove("show"),
        5e3,
      ));
  }
  if (
    (h.addEventListener("click", () => {
      if (((t = d.value), !/\S+@\S+\.\S+/.test(t)))
        return ((d.style.borderBottomColor = "#b3110d"), void d.focus());
      var t;
      // Invio nativo del modulo: una vera navigazione di pagina, come nella
      // Stanza 01. Il vecchio invio dentro un iframe nascosto poteva fallire
      // in silenzio (Shopify blocca le sue pagine dentro iframe e può
      // chiedere un captcha che nell'iframe nessuno vede). Shopify elabora
      // l'iscrizione e torna qui grazie a "return_to" (vedi ?entered=1).
      const entryFormEl = document.getElementById("entry-form");
      if (entryFormEl) {
        // L'ingresso si ricorda già qui, prima dell'invio: se il ritorno
        // da Shopify perde ?entered=1 (captcha, email già iscritta, redirect
        // diverso) alla visita dopo si entra comunque senza rifare il modulo.
        try {
          localStorage.setItem(ENTRY_KEY, "1");
        } catch (err) {}
        try {
          ((h.disabled = !0), (h.textContent = "Entering…"), entryFormEl.submit());
        } catch (err) {
          (console.error("Errore invio modulo email a Shopify:", err),
            dismissEntry());
        }
      } else {
        (console.warn(
          "Modulo #entry-form non trovato: email non inviata a Shopify.",
        ),
          dismissEntry());
      }
    }),
    d.addEventListener("input", () => {
      d.style.borderBottomColor = "";
    }),
    d.addEventListener("keydown", (t) => {
      "Enter" === t.key && (t.preventDefault(), h.click());
    }),
    "undefined" == typeof THREE)
  )
    throw (
      y(
        "The room couldn't load. Check your connection, then reload the page.",
        !0,
      ),
      new Error("THREE not loaded")
    );
  // Ritorno da Shopify dopo l'iscrizione: si entra direttamente, si
  // ricorda l'iscrizione e si ripulisce l'URL. Chi si era già iscritto in
  // una visita precedente entra subito, senza reinserire l'email.
  if (/[?&](entered=1|customer_posted=true)\b/.test(window.location.search)) {
    try {
      localStorage.setItem(ENTRY_KEY, "1");
    } catch (err) {}
    (dismissEntry(),
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.hash,
      ));
  } else {
    let alreadyIn = !1;
    try {
      alreadyIn = "1" === localStorage.getItem(ENTRY_KEY);
    } catch (err) {}
    alreadyIn && dismissEntry();
  }

  // --- Setup scena Three.js ----------------------------------------------
  const R = t.width,
    H = t.depth,
    x = t.height,
    S = document.getElementById("canvas-wrap"),
    v = new THREE.Scene(),
    FOG_COLOR = 0x11151a; // foschia notturna, quasi nera, che inghiotte lo skyline
  ((v.background = new THREE.Color(0x030405)), // notte fonda oltre la finestra
    (v.fog = new THREE.Fog(FOG_COLOR, H / 2 + 2, H / 2 + 95)));
  if (THREE.RectAreaLightUniformsLib) THREE.RectAreaLightUniformsLib.init();
  // ot: callback eseguite ad ogni frame — utile per animare elementi (es.
  // le nuvole che passano tra i palazzi). pt: posizioni {x, z} che il
  // visitatore deve evitare durante il movimento (es. la scrivania).
  const ot = [],
    pt = [],
    OBSTACLE_RADIUS = 0.55; // era 1.1: teneva il visitatore troppo lontano dalla scrivania
  const I = new THREE.PerspectiveCamera(
    62,
    window.innerWidth / window.innerHeight,
    0.1,
    400,
  );
  I.rotation.order = "YXZ";
  const P = new THREE.WebGLRenderer({
    antialias: !0,
    logarithmicDepthBuffer: !0,
  });
  // Su touch screen pixel ratio limitato a 1.5: differenza visiva minima,
  // molto meno carico sulla GPU del telefono.
  (P.setPixelRatio(Math.min(window.devicePixelRatio, isTouch ? 1.5 : 2)),
    P.setSize(window.innerWidth, window.innerHeight),
    (P.shadowMap.enabled = !0),
    (P.shadowMap.type = THREE.PCFSoftShadowMap),
    (P.outputEncoding = THREE.sRGBEncoding),
    S.appendChild(P.domElement));
  // --- Resa cinematografica ---------------------------------------------
  // exposure: luminosità generale dopo il tone mapping (1 = neutro).
  // lookSmoothing: più alto = camera più reattiva, più basso = più inerzia.
  const CINE = { exposure: 1.3, lookSmoothing: 12, breath: 0.011 };
  ((P.toneMapping = THREE.ACESFilmicToneMapping),
    (P.toneMappingExposure = CINE.exposure));

  // --- Profondità di campo: nitido dentro, sfocato oltre il vetro --------
  // Se gli script di postprocessing non si caricano (CDN lento/bloccato),
  // si ricade sul render diretto — la stanza resta comunque funzionante.
  let composer = null,
    bokehPass = null;
  // Su touch la profondità di campo è disattivata: è il passaggio più
  // pesante di tutta la stanza e sul telefono si nota poco.
  if (!isTouch && THREE.EffectComposer && THREE.RenderPass && THREE.BokehPass) {
    try {
      (composer = new THREE.EffectComposer(P)),
        composer.addPass(new THREE.RenderPass(v, I)),
        (bokehPass = new THREE.BokehPass(v, I, {
          focus: 9,
          aperture: 0.00018,
          maxblur: 0.006,
          width: window.innerWidth,
          height: window.innerHeight,
        })),
        composer.addPass(bokehPass);
    } catch (err) {
      (console.warn("Depth of field non disponibile:", err),
        (composer = null),
        (bokehPass = null));
    }
  }

  // --- Texture procedurale: cemento scuro con imperfezioni e crepe -------
  // Niente texture esterne da caricare: tutto disegnato su canvas, così la
  // stanza resta pesante zero e coerente su qualunque dispositivo.
  function concreteTexture(opts) {
    const o = document.createElement("canvas"),
      W = (o.width = 1024),
      Hh = (o.height = 1024),
      cx = o.getContext("2d"),
      base = opts.base || "#232326",
      dark = opts.dark || "#141416",
      light = opts.light || "#2f2f32";
    (cx.fillStyle = base), cx.fillRect(0, 0, W, Hh);
    // chiazze morbide di umidità/usura
    for (let i = 0; i < 22; i++) {
      const px = Math.random() * W,
        py = Math.random() * Hh,
        r = 60 + Math.random() * 220,
        g = cx.createRadialGradient(px, py, 0, px, py, r);
      (g.addColorStop(0, "rgba(0,0,0," + (0.05 + Math.random() * 0.1) + ")"),
        g.addColorStop(1, "rgba(0,0,0,0)"),
        (cx.fillStyle = g),
        cx.fillRect(px - r, py - r, r * 2, r * 2));
    }
    // grana/rumore fine
    const img = cx.getImageData(0, 0, W, Hh),
      d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const n = (Math.random() - 0.5) * 18;
      ((d[i] += n), (d[i + 1] += n), (d[i + 2] += n));
    }
    cx.putImageData(img, 0, 0);
    // crepe sottili, irregolari
    cx.strokeStyle = dark;
    for (let i = 0; i < (opts.cracks || 5); i++) {
      cx.lineWidth = 0.6 + Math.random() * 1.4;
      cx.globalAlpha = 0.35 + Math.random() * 0.3;
      let px = Math.random() * W,
        py = Math.random() * Hh;
      cx.beginPath(), cx.moveTo(px, py);
      const segs = 5 + Math.floor(Math.random() * 6);
      for (let s = 0; s < segs; s++) {
        (px += (Math.random() - 0.5) * 140), (py += (Math.random() - 0.5) * 140);
        cx.lineTo(px, py);
      }
      cx.stroke();
    }
    cx.globalAlpha = 1;
    // qualche filo di luce/consumo più chiaro, radente
    cx.strokeStyle = light;
    for (let i = 0; i < 8; i++) {
      cx.lineWidth = 0.5 + Math.random() * 1;
      cx.globalAlpha = 0.06 + Math.random() * 0.06;
      const py = Math.random() * Hh;
      cx.beginPath(), cx.moveTo(0, py), cx.lineTo(W, py + (Math.random() - 0.5) * 40), cx.stroke();
    }
    cx.globalAlpha = 1;
    const tx = new THREE.CanvasTexture(o);
    return ((tx.wrapS = tx.wrapT = THREE.RepeatWrapping), tx);
  }
  const wallTexColor = concreteTexture({
      base: "#28282b",
      dark: "#111113",
      light: "#38383c",
      cracks: 6,
    }),
    floorTexColor = concreteTexture({
      base: "#141416",
      dark: "#050506",
      light: "#232326",
      cracks: 3,
    });
  wallTexColor.repeat.set(2, 1);
  floorTexColor.repeat.set(3, 3.6);
  const floorMat = new THREE.MeshStandardMaterial({
      map: floorTexColor,
      color: 0x9a9a9c, // moltiplica la texture: pavimento scuro, riflessi minimi
      roughness: 0.62,
      metalness: 0.12,
    }),
    wallMat = new THREE.MeshStandardMaterial({
      map: wallTexColor,
      color: 0xaaaaac,
      roughness: 0.96,
      metalness: 0,
    }),
    ceilMat = new THREE.MeshStandardMaterial({
      map: wallTexColor,
      color: 0x6c6c70,
      roughness: 0.98,
      metalness: 0,
    }),
    D = new THREE.Mesh(new THREE.PlaneGeometry(R, H), floorMat);
  ((D.rotation.x = -Math.PI / 2), (D.receiveShadow = !0), v.add(D));
  const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(R, H), ceilMat);
  ((ceiling.rotation.x = Math.PI / 2), (ceiling.position.y = x), v.add(ceiling));
  // Pareti nord/sud (corrono lungo X, larghezza R) e ovest/est (corrono
  // lungo Z, larghezza H) — separate per restare corrette anche se in
  // futuro la stanza non fosse quadrata (R !== H).
  function wallNS(x0, z0, ry) {
    const wMesh = new THREE.Mesh(new THREE.PlaneGeometry(R, x), wallMat);
    (wMesh.position.set(x0, x / 2, z0),
      (wMesh.rotation.y = ry),
      (wMesh.receiveShadow = !0),
      v.add(wMesh));
  }
  function wallEW(x0, z0, ry) {
    const wMesh = new THREE.Mesh(new THREE.PlaneGeometry(H, x), wallMat);
    (wMesh.position.set(x0, x / 2, z0),
      (wMesh.rotation.y = ry),
      (wMesh.receiveShadow = !0),
      v.add(wMesh));
  }
  (wallNS(0, H / 2, Math.PI), // parete nord, alle spalle del visitatore all'ingresso
    wallEW(-R / 2, 0, Math.PI / 2), // parete ovest
    wallEW(R / 2, 0, -Math.PI / 2)); // parete est
  // La parete sud non è un pannello pieno: è la finestra. Costruita come
  // una cornice di cemento (4 strisce) che lascia un'unica grande apertura,
  // quasi quanto la parete stessa — nessun infisso, nessuna suddivisione.
  const winW = R * 0.82,
    winH = x * 0.7,
    sill = 0.68,
    zWall = -H / 2;
  (function buildWindowWall() {
    const leftW = (R - winW) / 2,
      strips = [
        // laterali: piena altezza
        { w: leftW, h: x, x: -R / 2 + leftW / 2, y: x / 2 },
        { w: leftW, h: x, x: R / 2 - leftW / 2, y: x / 2 },
        // sopra e sotto l'apertura
        { w: winW, h: x - sill - winH, x: 0, y: sill + winH + (x - sill - winH) / 2 },
        { w: winW, h: sill, x: 0, y: sill / 2 },
      ];
    strips.forEach((s) => {
      if (s.w <= 0.01 || s.h <= 0.01) return;
      const m = new THREE.Mesh(new THREE.PlaneGeometry(s.w, s.h), wallMat);
      (m.position.set(s.x, s.y, zWall), (m.receiveShadow = !0), v.add(m));
    });
    // profondità dello squarcio murario (spessore percepito del muro)
    const jamb = new THREE.Mesh(
      new THREE.PlaneGeometry(winW, 0.55),
      new THREE.MeshStandardMaterial({ color: 0x151517, roughness: 0.95 }),
    );
    (jamb.position.set(0, sill + winH, zWall + 0.28),
      (jamb.rotation.x = Math.PI / 2),
      v.add(jamb));
  })();
  // Vetro: quasi trasparente, un velo freddo appena percettibile.
  const glass = new THREE.Mesh(
    new THREE.PlaneGeometry(winW - 0.06, winH - 0.06),
    new THREE.MeshPhysicalMaterial({
      color: 0xaeb9c2,
      transparent: !0,
      opacity: 0.06,
      roughness: 0.05,
      metalness: 0,
      side: THREE.DoubleSide,
    }),
  );
  (glass.position.set(0, sill + winH / 2, zWall + 0.05), v.add(glass));

  // --- Tende, raccolte ai lati della finestra -----------------------------
  // Stesso rosso del tappeto. Aperte: tirate solo agli estremi
  // dell'apertura, non coprono la vista sulla città.
  (function buildCurtains() {
    const curtainMat = new THREE.MeshStandardMaterial({
        color: 0x5c1015,
        roughness: 0.88,
        metalness: 0,
      }),
      curtainH = x - 0.1, // quasi tutta l'altezza della stanza
      baseX = winW / 2 + 0.14; // appena oltre il bordo della finestra
    [-1, 1].forEach((side) => {
      const group = new THREE.Group();
      for (let i = 0; i < 5; i++) {
        const r = 0.068 + (i % 2 === 0 ? 0.016 : 0),
          fold = new THREE.Mesh(
            new THREE.CylinderGeometry(r, r * 0.92, curtainH, 12),
            curtainMat,
          );
        (fold.position.set(side * (baseX + i * 0.052), curtainH / 2, 0),
          (fold.castShadow = !0),
          (fold.receiveShadow = !0),
          group.add(fold));
      }
      // fascia che raccoglie la tenda, a circa un terzo dell'altezza
      const tieback = new THREE.Mesh(
        new THREE.TorusGeometry(0.15, 0.02, 8, 20),
        curtainMat,
      );
      (tieback.rotation.x = Math.PI / 2,
        tieback.position.set(side * (baseX + 0.09), curtainH * 0.36, 0),
        group.add(tieback));
      (group.position.z = zWall + 0.24, v.add(group));
    });
  })();

  // --- Città di cemento, vista dall'alto, oltre la finestra --------------
  // La stanza è in quota: il livello "terra" della città è molto più in
  // basso del pavimento, così guardando fuori si vedono soprattutto i
  // tetti dei palazzi più bassi, con lo skyline che sale ai lati.
  const CITY_GROUND_Y = -22;
  // Texture di facciata: cemento scuro con una griglia di finestre, quasi
  // tutte spente, qualcuna accesa — colore della luce leggermente diverso
  // da edificio a edificio (alcune più calde, alcune più fredde/neon).
  // Risoluzione alta e più passaggi di dettaglio (cornicioni, marcapiani,
  // vetri leggermente diversi l'uno dall'altro, colatura sul cemento) così
  // regge bene anche sui palazzi più vicini alla finestra.
  function buildingFacadeTexture(cols, rows, warmth, res) {
    res = res || 768;
    const o = document.createElement("canvas"),
      W = (o.width = res),
      Hh = (o.height = res),
      cx = o.getContext("2d");
    (cx.fillStyle = "#1b1d20"), cx.fillRect(0, 0, W, Hh);
    // grana di cemento
    for (let i = 0; i < Math.round(2600 * (res / 768)); i++) {
      cx.fillStyle = "rgba(0,0,0," + (0.025 + Math.random() * 0.05) + ")";
      cx.fillRect(Math.random() * W, Math.random() * Hh, 2, 2);
    }
    // colature verticali di sporco/pioggia, come sui muri interni ma più rade
    cx.strokeStyle = "rgba(0,0,0,0.16)";
    for (let i = 0; i < 10; i++) {
      const sx = Math.random() * W;
      cx.lineWidth = 1 + Math.random() * 2.5;
      cx.beginPath(), cx.moveTo(sx, 0);
      let px = sx;
      for (let s = 0; s < 6; s++) {
        (px += (Math.random() - 0.5) * 30), cx.lineTo(px, ((s + 1) / 6) * Hh);
      }
      cx.stroke();
    }
    const cw = W / cols,
      rh = Hh / rows,
      pad = Math.min(cw, rh) * 0.2;
    // marcapiani: una fascia leggermente più chiara/scura a ogni piano
    for (let r = 0; r < rows; r++) {
      cx.fillStyle = Math.random() < 0.5 ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.03)";
      cx.fillRect(0, r * rh, W, Math.max(2, rh * 0.05));
    }
    // mullion verticali tra le colonne di finestre
    cx.fillStyle = "rgba(0,0,0,0.22)";
    for (let c = 1; c < cols; c++) cx.fillRect(c * cw - 1.5, 0, 3, Hh);
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++) {
        const lit = Math.random() < 0.15,
          x0 = c * cw + pad,
          y0 = r * rh + pad,
          w0 = cw - pad * 2,
          h0 = rh - pad * 2;
        if (lit) {
          const R0 = warmth > 0.5 ? 255 : 210 + Math.floor(Math.random() * 30),
            G0 = warmth > 0.5 ? 190 + Math.floor(Math.random() * 40) : 220 + Math.floor(Math.random() * 25),
            B0 = warmth > 0.5 ? 120 + Math.floor(Math.random() * 60) : 235;
          cx.fillStyle = `rgba(${R0},${G0},${B0},0.92)`;
          cx.fillRect(x0, y0, w0, h0);
          // leggero alone attorno alla finestra accesa
          cx.fillStyle = `rgba(${R0},${G0},${B0},0.14)`;
          cx.fillRect(x0 - 3, y0 - 3, w0 + 6, h0 + 6);
        } else {
          // vetro spento, ma non tutti uguali: una leggera variazione
          // blu/grigia da riflesso del cielo notturno
          const gl = 5 + Math.floor(Math.random() * 6);
          cx.fillStyle = `rgba(${gl + 2},${gl + 4},${gl + 9},0.9)`;
          cx.fillRect(x0, y0, w0, h0);
        }
        // bordo infisso, per dare spessore/rilievo alla finestra
        (cx.strokeStyle = "rgba(0,0,0,0.35)"),
          (cx.lineWidth = 1),
          cx.strokeRect(x0, y0, w0, h0);
        // filo di luce in alto a sinistra, effetto smussato
        (cx.strokeStyle = "rgba(255,255,255,0.05)"),
          cx.beginPath(),
          cx.moveTo(x0, y0 + h0),
          cx.lineTo(x0, y0),
          cx.lineTo(x0 + w0, y0),
          cx.stroke();
      }
    // vignettatura ai bordi: la facciata si scurisce leggermente agli
    // spigoli, aiuta a leggere il volume anche da lontano nella nebbia
    const vg = cx.createRadialGradient(W / 2, Hh / 2, Hh * 0.35, W / 2, Hh / 2, Hh * 0.75);
    (vg.addColorStop(0, "rgba(0,0,0,0)"),
      vg.addColorStop(1, "rgba(0,0,0,0.4)"),
      (cx.fillStyle = vg),
      cx.fillRect(0, 0, W, Hh));
    const tx = new THREE.CanvasTexture(o);
    return ((tx.wrapS = tx.wrapT = THREE.ClampToEdgeWrapping), tx);
  }
  const cityConcrete = concreteTexture({
    base: "#22262a",
    dark: "#0d0f11",
    light: "#31363b",
    cracks: 2,
  });
  (function buildFoggyCity() {
    const cityGroup = new THREE.Group(),
      rng = (function seeded(seed) {
        let s = seed;
        return () => ((s = (s * 16807) % 2147483647), (s - 1) / 2147483646);
      })(42),
      roofMat = new THREE.MeshBasicMaterial({ color: 0x24282c, fog: !0 }),
      baseSideTint = 0x3a4048,
      beacons = [],
      // Corridoio libero davanti alla torre dell'insegna: nessun edificio
      // casuale può cadere qui, altrimenti la scritta resta nascosta.
      SIGN_X = -0.8,
      SIGN_HALF_CLEAR = 5.5;
    function makeBuilding(depth, bx, tier) {
      const bw = (tier ? 0.55 : 1) * (3 + rng() * 8),
        bd = (tier ? 0.55 : 1) * (3 + rng() * 8),
        bh = (tier ? 0.6 : 1) * (3 + rng() * 26 * Math.max(0.25, 1 - depth / 140)),
        cols = 3 + Math.floor(rng() * 3),
        rows = 4 + Math.floor(rng() * 6),
        warmth = rng(),
        // su touch texture più leggere: ~100 palazzi a 1024px erano centinaia
        // di MB di memoria grafica, rischio concreto di crash su iPhone
        facadeRes = isTouch
          ? depth < 30
            ? 512
            : depth < 70
              ? 320
              : 192
          : depth < 30
            ? 1024
            : depth < 70
              ? 640
              : 384,
        tint = new THREE.Color(baseSideTint).offsetHSL(
          (rng() - 0.5) * 0.05,
          (rng() - 0.5) * 0.12,
          (rng() - 0.5) * 0.07,
        ),
        sideMat = new THREE.MeshBasicMaterial({
          map: cityConcrete,
          color: tint,
          fog: !0,
        }),
        facadeMat = new THREE.MeshBasicMaterial({
          map: buildingFacadeTexture(cols, rows, warmth, facadeRes),
          fog: !0,
        }),
        b = new THREE.Mesh(
          new THREE.BoxGeometry(bw, bh, bd),
          // ordine facce BoxGeometry: +x,-x,+y(tetto),-y,+z(verso la stanza),-z
          [sideMat, sideMat, roofMat, sideMat, facadeMat, sideMat],
        );
      return { mesh: b, bw, bd, bh };
    }
    for (let i = 0; i < 85; i++) {
      // distribuzione sbilanciata verso i palazzi vicini, quelli davvero
      // visibili — il grosso della nebbia si mangia comunque tutto oltre
      const depth = 6 + Math.pow(rng(), 1.6) * 128,
        bx = (rng() - 0.5) * (R + depth * 1.7);
      if (Math.abs(bx - SIGN_X) < SIGN_HALF_CLEAR) continue; // corridoio della torre, resta libero
      const base = makeBuilding(depth, bx, !1),
        baseY = CITY_GROUND_Y + base.bh / 2;
      (base.mesh.position.set(bx, baseY, zWall - depth), cityGroup.add(base.mesh));
      // arretramento a torre: un volume più piccolo impilato sopra, per
      // rompere la monotonia dei blocchi tutti uguali
      if (depth < 80 && rng() < 0.32) {
        const tier = makeBuilding(depth, bx, !0);
        (tier.mesh.position.set(
          bx + (rng() - 0.5) * base.bw * 0.3,
          CITY_GROUND_Y + base.bh + tier.bh / 2,
          zWall - depth + (rng() - 0.5) * base.bd * 0.3,
        ),
          cityGroup.add(tier.mesh));
        // faro di segnalazione lampeggiante sulle torri più alte e vicine
        if (depth < 45 && rng() < 0.4) {
          const beacon = new THREE.Mesh(
            new THREE.SphereGeometry(0.09, 8, 8),
            new THREE.MeshBasicMaterial({ color: 0xff3b30, fog: !0 }),
          );
          (beacon.position.set(
            tier.mesh.position.x,
            CITY_GROUND_Y + base.bh + tier.bh + 0.1,
            tier.mesh.position.z,
          ),
            cityGroup.add(beacon),
            beacons.push(beacon));
        }
      }
      // impianti sul tetto, visibili dall'alto sui palazzi più vicini
      if (depth < 60 && rng() < 0.5) {
        const water = rng() < 0.35,
          unit = new THREE.Mesh(
            water
              ? new THREE.CylinderGeometry(base.bw * 0.1, base.bw * 0.11, 0.5, 10)
              : new THREE.BoxGeometry(base.bw * 0.22, 0.5, base.bd * 0.22),
            roofMat,
          );
        (unit.position.set(
          bx + (rng() - 0.5) * base.bw * 0.4,
          baseY + base.bh / 2 + 0.25,
          zWall - depth + (rng() - 0.5) * base.bd * 0.4,
        ),
          cityGroup.add(unit));
      }
    }

    // --- Insegna sull'edificio di fronte alla finestra ------------------
    // Una torre dedicata, centrata nel campo visivo di chi entra ed
    // guarda dritto avanti: l'unica scritta nella nebbia.
    (function buildSignTower() {
      const signDepth = 15, // molto più vicina di prima: era illeggibile a 42
        signX = SIGN_X,
        bw = 9,
        bd = 6,
        bh = 27,
        cols = 4,
        rows = 9,
        warmth = 0.5,
        tint = new THREE.Color(baseSideTint),
        sideMat = new THREE.MeshBasicMaterial({ map: cityConcrete, color: tint, fog: !0 }),
        facadeMat = new THREE.MeshBasicMaterial({
          map: buildingFacadeTexture(cols, rows, warmth, 1024),
          fog: !0,
        }),
        tower = new THREE.Mesh(
          new THREE.BoxGeometry(bw, bh, bd),
          [sideMat, sideMat, roofMat, sideMat, facadeMat, sideMat],
        );
      tower.position.set(signX, CITY_GROUND_Y + bh / 2, zWall - signDepth);
      cityGroup.add(tower);

      // targa dell'insegna: solo le lettere al neon, sfondo trasparente.
      // Prima c'era un pannello nero pieno dietro il testo — è quello che
      // la faceva sembrare un banner incollato sopra l'edificio invece
      // che un'insegna montata sulla facciata. Ora la facciata (cemento e
      // finestre) resta visibile dietro le lettere.
      const sc = document.createElement("canvas"),
        SW = (sc.width = 2048),
        SH = (sc.height = 340),
        scx = sc.getContext("2d");
      scx.textAlign = "center";
      scx.textBaseline = "middle";
      scx.font = '700 138px "Helvetica Neue", Arial, sans-serif';
      (scx.shadowColor = "#ff2d3d"),
        (scx.shadowBlur = 44),
        (scx.fillStyle = "#ff5a63");
      for (let i = 0; i < 3; i++)
        scx.fillText("ANATOMIA DEL MARGINE", SW / 2, SH / 2 + 8);
      (scx.shadowBlur = 10), (scx.fillStyle = "#ffe4e2");
      scx.fillText("ANATOMIA DEL MARGINE", SW / 2, SH / 2 + 8);
      scx.shadowBlur = 0;
      const signTex = new THREE.CanvasTexture(sc);
      signTex.encoding = THREE.sRGBEncoding;
      const signW = bw * 0.86,
        signH = signW * (SH / SW),
        signY = CITY_GROUND_Y + bh - 3.4,
        signMat = new THREE.MeshBasicMaterial({
          map: signTex,
          toneMapped: !1,
          transparent: !0,
          depthWrite: !1,
          fog: !0,
        }),
        signPlane = new THREE.Mesh(new THREE.PlaneGeometry(signW, signH), signMat);
      signPlane.position.set(signX, signY, zWall - signDepth + bd / 2 + 0.04);
      cityGroup.add(signPlane);

      // bagliore morbido dietro l'insegna, che respira lentissimo — non
      // un neon che sfarfalla, un lume fermo che pulsa appena
      const glowTex = (function () {
          const g = document.createElement("canvas"),
            GW = (g.width = 512),
            GH = (g.height = 256),
            gcx = g.getContext("2d"),
            grad = gcx.createRadialGradient(GW / 2, GH / 2, 0, GW / 2, GH / 2, GW / 2);
          (grad.addColorStop(0, "rgba(255,60,70,0.55)"),
            grad.addColorStop(0.5, "rgba(255,60,70,0.22)"),
            grad.addColorStop(1, "rgba(255,60,70,0)"),
            (gcx.fillStyle = grad),
            gcx.fillRect(0, 0, GW, GH));
          return new THREE.CanvasTexture(g);
        })(),
        glowMat = new THREE.MeshBasicMaterial({
          map: glowTex,
          toneMapped: !1,
          transparent: !0,
          opacity: 0.5,
          depthWrite: !1,
          fog: !0,
        }),
        glowPlane = new THREE.Mesh(
          new THREE.PlaneGeometry(signW * 2.1, signH * 2.6),
          glowMat,
        );
      glowPlane.position.set(signX, signY, zWall - signDepth + bd / 2 + 0.02);
      cityGroup.add(glowPlane);
      ot.push({
        update: (elapsed) => {
          glowMat.opacity = 0.4 + 0.15 * Math.sin(elapsed * 0.35);
        },
      });

      // --- Riflesso dell'insegna sul vetro --------------------------------
      // Una copia fantasma, capovolta e sfalsata, appesa alla superficie
      // interna del vetro: come ci si aspetterebbe di intravedere
      // un'insegna riflessa guardando una finestra di sera.
      // Nessun capovolgimento: un riflesso su un vetro verticale non
      // ribalta il testo, lo sdoppia appena, leggermente sfalsato e più
      // tenue — deve restare leggibile, non un rebus.
      const reflMat = new THREE.MeshBasicMaterial({
          map: signTex,
          toneMapped: !1,
          transparent: !0,
          opacity: 0.22,
          depthWrite: !1,
          fog: !1,
          blending: THREE.AdditiveBlending,
        }),
        reflPlane = new THREE.Mesh(new THREE.PlaneGeometry(signW, signH), reflMat);
      (reflPlane.position.set(signX + 0.12, signY + 0.04, zWall + 0.06),
        v.add(reflPlane));

      // --- Riflesso rosso sul pavimento, appena dentro la finestra --------
      // Una macchia di luce calda che si posa sul cemento lucido, come se
      // l'insegna in lontananza si specchiasse debolmente sul pavimento.
      const poolCanvas = document.createElement("canvas");
      (poolCanvas.width = poolCanvas.height = 256);
      const pcx = poolCanvas.getContext("2d"),
        pg = pcx.createRadialGradient(128, 128, 0, 128, 128, 128);
      (pg.addColorStop(0, "rgba(255,55,65,0.4)"),
        pg.addColorStop(0.55, "rgba(255,55,65,0.14)"),
        pg.addColorStop(1, "rgba(255,55,65,0)"),
        (pcx.fillStyle = pg),
        pcx.fillRect(0, 0, 256, 256));
      const poolTex = new THREE.CanvasTexture(poolCanvas),
        poolMat = new THREE.MeshBasicMaterial({
          map: poolTex,
          transparent: !0,
          opacity: 0.55,
          depthWrite: !1,
          fog: !1,
          blending: THREE.AdditiveBlending,
        }),
        poolPlane = new THREE.Mesh(new THREE.PlaneGeometry(7, 9), poolMat);
      (poolPlane.rotation.x = -Math.PI / 2),
        poolPlane.position.set(signX + 0.5, 0.008, zWall + 4.2),
        v.add(poolPlane);
      ot.push({
        update: (elapsed) => {
          poolMat.opacity = 0.45 + 0.1 * Math.sin(elapsed * 0.35 + 1.4);
        },
      });
    })();

    // un velo di bruma più fitto vicino ai tetti, davanti allo skyline
    const mist = new THREE.Mesh(
      new THREE.PlaneGeometry(R * 6, 30),
      new THREE.MeshBasicMaterial({
        color: FOG_COLOR,
        transparent: !0,
        opacity: 0.4,
        fog: !1,
      }),
    );
    (mist.position.set(0, CITY_GROUND_Y + 14, zWall - 30), cityGroup.add(mist));
    v.add(cityGroup);
    if (beacons.length)
      ot.push({
        update: (elapsed) => {
          const k = 0.55 + 0.45 * Math.sin(elapsed * 2.4);
          beacons.forEach((bcn) => bcn.scale.setScalar(0.7 + k * 0.6));
        },
      });

    // --- Nuvole che passano lente tra i palazzi -----------------------
    // Sagome morbide ma dense abbastanza da leggersi contro il cielo
    // scuro: niente attenuazione da nebbia (altrimenti spariscono contro
    // il fondo quasi nero), colore chiaro per contrasto reale.
    function cloudTexture() {
      const o = document.createElement("canvas"),
        W = (o.width = 320),
        Hh = (o.height = 320),
        cx = o.getContext("2d");
      // più lobi sovrapposti per una forma meno perfettamente rotonda
      const blobs = [
        [160, 160, 150],
        [95, 185, 95],
        [235, 175, 105],
        [150, 110, 100],
        [190, 210, 90],
      ];
      blobs.forEach(([bx, by, br]) => {
        const g = cx.createRadialGradient(bx, by, 0, bx, by, br);
        (g.addColorStop(0, "rgba(205,212,222,0.8)"),
          g.addColorStop(0.55, "rgba(170,180,194,0.45)"),
          g.addColorStop(1, "rgba(170,180,194,0)"),
          (cx.fillStyle = g),
          cx.fillRect(bx - br, by - br, br * 2, br * 2));
      });
      return new THREE.CanvasTexture(o);
    }
    const cloudTex = cloudTexture(),
      clouds = [];
    for (let i = 0; i < 15; i++) {
      const mat = new THREE.MeshBasicMaterial({
          map: cloudTex,
          transparent: !0,
          opacity: 0.55 + rng() * 0.3,
          depthWrite: !1,
          fog: !1,
        }),
        w0 = 16 + rng() * 26,
        cloud = new THREE.Mesh(new THREE.PlaneGeometry(w0, w0 * 0.52), mat);
      (cloud.position.set(
        (rng() - 0.5) * (R + 90),
        CITY_GROUND_Y + 12 + rng() * 26,
        zWall - 14 - rng() * 100,
      ),
        (cloud.userData.speed = 0.22 + rng() * 0.42),
        (cloud.userData.rangeX = R / 2 + 60),
        cityGroup.add(cloud),
        clouds.push(cloud));
    }
    ot.push({
      update: (elapsed, dt) => {
        clouds.forEach((c) => {
          ((c.position.x += c.userData.speed * dt),
            c.position.x > c.userData.rangeX &&
              (c.position.x = -c.userData.rangeX));
        });
      },
    });
  })();

  // --- Illuminazione statica: in penombra, ma visibile ---------------------
  const hemi = new THREE.HemisphereLight(0x4c5860, 0x1c1d20, n.ambientIntensity);
  v.add(hemi);
  // Luce d'ambiente fredda e diffusa che entra dall'apertura: un'area
  // light posizionata esattamente sulla finestra, rivolta verso la stanza.
  if (THREE.RectAreaLight) {
    const winLight = new THREE.RectAreaLight(0x8fa4b8, 7.8, winW, winH);
    (winLight.position.set(0, sill + winH / 2, zWall + 0.15),
      winLight.lookAt(0, e.eyeHeight, H / 2),
      v.add(winLight));
  }
  // Una luce direzionale morbida, bassa intensità e volutamente fuori asse:
  // ombre lunghe e oblique sul pavimento vuoto, come in una piazza
  // metafisica — non un'illuminazione "corretta" da studio fotografico.
  const key = new THREE.DirectionalLight(0x8fa0b0, n.lightIntensity * 0.7);
  (key.position.set(R * 0.32, x * 0.62, zWall + 1.2),
    key.target.position.set(-R * 0.22, 0, H / 2 - 1),
    (key.castShadow = !0),
    key.shadow.mapSize.set(1024, 1024),
    (key.shadow.camera.left = -R / 2 - 1),
    (key.shadow.camera.right = R / 2 + 1),
    (key.shadow.camera.top = H / 2 + 1),
    (key.shadow.camera.bottom = -H / 2 - 1),
    (key.shadow.camera.near = 1),
    (key.shadow.camera.far = H + 10),
    (key.shadow.bias = -0.0015),
    v.add(key),
    v.add(key.target));

  // --- Pulviscolo nel fascio della finestra -------------------------------
  // Pochi punti che galleggiano lentissimi nella luce che entra dalla
  // finestra: l'unico movimento nella stanza oltre al respiro dell'insegna
  // in lontananza. Un THREE.Points, leggerissimo per le prestazioni.
  (function buildDustMotes() {
    const count = 46,
      positions = new Float32Array(count * 3),
      speeds = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      (positions[i * 3] = (Math.random() - 0.5) * winW * 0.9),
        (positions[i * 3 + 1] = sill + Math.random() * winH),
        (positions[i * 3 + 2] = zWall + 0.5 + Math.random() * (H - 1.5)),
        (speeds[i] = 0.05 + Math.random() * 0.09);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const dustCanvas = document.createElement("canvas");
    (dustCanvas.width = dustCanvas.height = 32);
    const dcx = dustCanvas.getContext("2d"),
      dg = dcx.createRadialGradient(16, 16, 0, 16, 16, 16);
    (dg.addColorStop(0, "rgba(230,225,215,0.9)"),
      dg.addColorStop(1, "rgba(230,225,215,0)"),
      (dcx.fillStyle = dg),
      dcx.fillRect(0, 0, 32, 32));
    const dustTex = new THREE.CanvasTexture(dustCanvas),
      dustMat = new THREE.PointsMaterial({
        map: dustTex,
        size: 0.028,
        transparent: !0,
        opacity: 0.32,
        depthWrite: !1,
        sizeAttenuation: !0,
      }),
      points = new THREE.Points(geo, dustMat);
    v.add(points);
    const posAttr = geo.attributes.position;
    ot.push({
      update: (elapsed, dt) => {
        for (let i = 0; i < count; i++) {
          let py = posAttr.getY(i) + speeds[i] * dt * 0.3;
          py > sill + winH && (py = sill);
          posAttr.setY(i, py);
          posAttr.setX(i, posAttr.getX(i) + Math.sin(elapsed * 0.2 + i) * 0.0006);
        }
        posAttr.needsUpdate = !0;
      },
    });
  })();

  // ot/pt/OBSTACLE_RADIUS sono dichiarati più sopra, subito dopo la scena,
  // così anche buildFoggyCity() e la scrivania possono usarli.

  // --- Cornici-prodotto sui muri -------------------------------------------
  let imgErrShown = !1;
  function $t(t, e) {
    if (t.imageUrl) {
      const n = new THREE.TextureLoader();
      (n.setCrossOrigin("anonymous"),
        n.load(
          t.imageUrl,
          (n) => {
            ((n.encoding = THREE.sRGBEncoding), e && e(n));
          },
          void 0,
          (e) => {
            (console.error("Immagine NON caricata per", t.title, t.imageUrl, e),
              imgErrShown ||
                ((imgErrShown = !0),
                y(
                  "Some artwork previews didn't load. Tap a frame to see the piece and its details.",
                )));
          },
        ));
    }
    return (function (t) {
      const e = document.createElement("canvas");
      ((e.width = 512), (e.height = 640));
      const n = e.getContext("2d"),
        a = "#" + t.toString(16).padStart(6, "0");
      ((n.fillStyle = a), n.fillRect(0, 0, e.width, e.height));
      const o = n.createLinearGradient(0, 0, e.width, e.height);
      (o.addColorStop(0, "rgba(255,255,255,0.04)"),
        o.addColorStop(1, "rgba(0,0,0,0.28)"),
        (n.fillStyle = o),
        n.fillRect(0, 0, e.width, e.height));
      const i = new THREE.CanvasTexture(e);
      return ((i.encoding = THREE.sRGBEncoding), i);
    })(t.color);
  }
  // Le cornici-prodotto sono appese a 3 delle 4 pareti (la parete nord è
  // riservata al cartello di uscita). Con più di 3 prodotti, questa
  // funzione li distribuisce in blocchi consecutivi lungo ciascuna parete
  // (invece di sovrapporli tutti negli stessi 3 punti), spaziandoli
  // uniformemente. Si adatta automaticamente al numero di prodotti in `l`.
  function Qt(index, total) {
    const walls = [
        { axis: "x", fixed: H / 2 - 0.05, ry: Math.PI }, // parete nord (alle spalle dell'ingresso)
        { axis: "z", fixed: -R / 2 + 0.05, ry: Math.PI / 2 }, // parete ovest
        { axis: "z", fixed: R / 2 - 0.05, ry: -Math.PI / 2 }, // parete est
      ],
      perWall = Math.ceil(total / walls.length),
      wallIndex = Math.min(walls.length - 1, Math.floor(index / perWall)),
      wall = walls[wallIndex],
      indexInWall = index - wallIndex * perWall,
      countInWall = Math.min(perWall, total - wallIndex * perWall),
      // lunghezza utile della parete, con 2 m di margine dagli angoli (prima
      // era fissa a 24 m, più lunga delle pareti di questa stanza)
      span = ("x" === wall.axis ? R : H) - 4,
      offset =
        countInWall <= 1
          ? 0
          : THREE.MathUtils.lerp(
              -span / 2,
              span / 2,
              indexInWall / (countInWall - 1),
            );
    return "x" === wall.axis
      ? { x: offset, z: wall.fixed, ry: wall.ry }
      : { x: wall.fixed, z: offset, ry: wall.ry };
  }
  const Jt = [],
    te = new THREE.Group();
  const CARMINE = "#6e1015"; // rosso carminio, opaco e un po' consumato — non acceso
  function ee(t) {
    // Targhetta museale: piccola piastra scura separata dalla cornice,
    // appesa sotto — non più la cornice stessa (quella ora è geometria
    // vera, non una texture piatta). Testo grassetto e ad alto contrasto:
    // la versione precedente (peso 300, sottilissima) era illeggibile a
    // distanza normale di visita.
    const n = 640,
      a = 160,
      o = document.createElement("canvas");
    ((o.width = n), (o.height = a));
    const i = o.getContext("2d");
    ((i.fillStyle = "#0c0c0d"), i.fillRect(0, 0, n, a));
    (i.strokeStyle = "rgba(255,255,255,0.1)"),
      (i.lineWidth = 2),
      i.strokeRect(1, 1, n - 2, a - 2);
    ((i.fillStyle = "rgba(245,245,246,0.97)"),
      (i.font = `600 52px "Helvetica Neue", Arial, sans-serif`),
      (i.textBaseline = "middle"),
      (i.letterSpacing = "0.5px"),
      i.fillText(t, 28, a / 2 + 2));
    const h = new THREE.CanvasTexture(o);
    return ((h.encoding = THREE.sRGBEncoding), h);
  }
  // Texture del legno verniciato rosso carminio: striature verticali
  // sottili + qualche sbeccatura, così la cornice legge come un oggetto
  // vero e non un rettangolo di colore piatto.
  const frameTex = (function () {
    const o = document.createElement("canvas"),
      W = (o.width = 256),
      Hh = (o.height = 256),
      cx = o.getContext("2d");
    (cx.fillStyle = CARMINE), cx.fillRect(0, 0, W, Hh);
    for (let i = 0; i < 60; i++) {
      const gx = Math.random() * W;
      (cx.strokeStyle =
        "rgba(" + (Math.random() < 0.5 ? "0,0,0," : "255,255,255,") +
        (0.04 + Math.random() * 0.06) + ")"),
        (cx.lineWidth = 0.6 + Math.random() * 1.4);
      cx.beginPath(), cx.moveTo(gx, 0), cx.lineTo(gx + (Math.random() - 0.5) * 12, Hh), cx.stroke();
    }
    for (let i = 0; i < 10; i++) {
      cx.fillStyle = "rgba(0,0,0," + (0.12 + Math.random() * 0.12) + ")";
      cx.fillRect(Math.random() * W, Math.random() * Hh, 2 + Math.random() * 5, 1 + Math.random() * 2);
    }
    const tx = new THREE.CanvasTexture(o);
    return ((tx.wrapS = tx.wrapT = THREE.RepeatWrapping), tx);
  })();
  const ne = new THREE.MeshStandardMaterial({
      map: frameTex,
      color: 0xb9848a, // moltiplica la texture: la mantiene satura ma non piatta
      roughness: 0.5,
      metalness: 0,
    }),
    productCount = l.length; // calcolato prima del forEach: al suo interno
  // esiste già una variabile locale chiamata "l" (il materiale del poster)
  // che oscurerebbe l'array prodotti per l'intero blocco.
  // Una modanatura vera, sporgente rispetto all'opera (che sta incassata
  // dentro), invece di una texture piatta dietro l'immagine — è questo che
  // fa leggere l'insieme come una cornice fisica e non come un adesivo.
  function frameStrip(w, h, x, y, mDepth) {
    const strip = new THREE.Mesh(new THREE.BoxGeometry(w, h, mDepth), ne);
    return (strip.position.set(x, y, mDepth / 2 - 0.008), (strip.castShadow = !0), strip);
  }
  (l.forEach((t, e) => {
    const n = Qt(e, productCount),
      a = 1.5 / (210 / 297), // altezza immagine (formato A3)
      imgW = 1.5,
      mw = 0.09, // larghezza della modanatura
      mDepth = 0.055; // sporgenza della cornice rispetto al quadro
    const l = new THREE.MeshStandardMaterial({
        map: $t(t, (t) => {
          ((l.map = t), (l.needsUpdate = !0));
        }),
        roughness: 0.88,
        toneMapped: !1,
      }),
      d = new THREE.Mesh(new THREE.PlaneGeometry(imgW, a), l);
    ((d.receiveShadow = !0),
      (d.userData = { type: "artwork", index: e }),
      Jt.push(d));
    // pannello di supporto, appena dietro il quadro
    const backing = new THREE.Mesh(
      new THREE.BoxGeometry(imgW, a, 0.02),
      new THREE.MeshStandardMaterial({ color: 0x141416, roughness: 0.9 }),
    );
    backing.position.z = -0.015;
    // 4 listelli della cornice, incastrati attorno al quadro e sporgenti
    // in avanti — stesso schema di costruzione usato per il vano finestra
    const top = frameStrip(imgW + mw * 2, mw, 0, a / 2 + mw / 2, mDepth),
      bottom = frameStrip(imgW + mw * 2, mw, 0, -a / 2 - mw / 2, mDepth),
      left = frameStrip(mw, a + mw * 2, -imgW / 2 - mw / 2, 0, mDepth),
      right = frameStrip(mw, a + mw * 2, imgW / 2 + mw / 2, 0, mDepth);
    // targhetta museale, piccola e separata, appesa sotto la cornice
    const plaqueW = 0.56,
      plaqueH = 0.14,
      plaque = new THREE.Mesh(
        new THREE.PlaneGeometry(plaqueW, plaqueH),
        new THREE.MeshBasicMaterial({ map: ee(t.title), toneMapped: !1 }),
      );
    plaque.position.set(0, -a / 2 - mw - 0.14, 0.02);
    const h = new THREE.Group();
    (h.add(backing), h.add(d), h.add(top), h.add(bottom), h.add(left), h.add(right), h.add(plaque));
    // Faretto LED da museo: corpo opaco sottile + striscia calda, puntato
    // sull'opera. Unica fonte di luce calda accanto alle finestre accese
    // in lontananza — il resto della stanza resta freddo.
    const frameTopY = a / 2 + mw,
      ledHousing = new THREE.Mesh(
        new THREE.BoxGeometry(imgW * 0.5, 0.035, 0.06),
        new THREE.MeshStandardMaterial({
          color: 0x0c0c0d,
          roughness: 0.35,
          metalness: 0.6,
        }),
      ),
      ledGlow = new THREE.Mesh(
        new THREE.PlaneGeometry(imgW * 0.44, 0.014),
        new THREE.MeshBasicMaterial({ color: 0xfff8ec }),
      ),
      ledLight = new THREE.SpotLight(
        0xfff2dc,
        6.5,
        3.6,
        Math.PI / 7.5,
        0.5,
        1.25,
      ),
      ledTarget = new THREE.Object3D();
    ((ledLight.castShadow = !1),
      ledHousing.position.set(0, frameTopY + 0.16, 0.1),
      ledGlow.position.set(0, frameTopY + 0.16, 0.132),
      ledLight.position.set(0, frameTopY + 0.15, 0.11),
      ledTarget.position.set(0, 0, mDepth),
      (ledLight.target = ledTarget),
      h.add(ledHousing),
      h.add(ledGlow),
      h.add(ledLight),
      h.add(ledTarget),
      h.position.set(n.x, 1.9, n.z),
      (h.rotation.y = n.ry),
      te.add(h));
  }),
    v.add(te));

  // Bagliore del libro all'hover: riempito da buildDesk(), animato da `ot`.
  let bookHover = null;
  // --- Scrivania al centro della stanza -----------------------------------
  // L'unico elemento nella stanza: una postazione di lavoro abbandonata,
  // rivolta verso la finestra. La lampada è l'unica luce calda a distanza
  // ravvicinata — un punto fermo nel freddo blu-grigio del resto.
  (function buildDesk() {
    const deskGroup = new THREE.Group(),
      metalMat = new THREE.MeshStandardMaterial({
        color: 0x0e0e10,
        roughness: 0.45,
        metalness: 0.55,
      }),
      woodMat = new THREE.MeshStandardMaterial({
        color: 0x22190f,
        roughness: 0.75,
        metalness: 0.05,
      }),
      deskTopW = 1.3,
      deskTopD = 0.62,
      deskH = 0.72;

    // --- Tappeto persiano, sotto scrivania e sedia --------------------
    // Circa 3 volte le dimensioni del tavolo, disegnato su canvas:
    // campo rosso, bordo ornato, medaglione centrale, motivi d'angolo.
    (function buildRug() {
      // Solo rosso, niente disegni geometrici: campo pieno con grana
      // tessile e una vignettatura morbida ai bordi, così non sembra un
      // rettangolo di colore piatto ma resta comunque un tappeto liscio.
      const o = document.createElement("canvas"),
        W = (o.width = 1024),
        Hh = (o.height = 640),
        cx = o.getContext("2d"),
        FIELD = "#5c1015";
      (cx.fillStyle = FIELD), cx.fillRect(0, 0, W, Hh);
      // grana tessile
      for (let i = 0; i < 6000; i++) {
        cx.fillStyle = "rgba(0,0,0," + (0.015 + Math.random() * 0.04) + ")";
        cx.fillRect(Math.random() * W, Math.random() * Hh, 2, 1);
      }
      // leggere striature di tessitura orizzontali
      cx.strokeStyle = "rgba(0,0,0,0.05)";
      for (let y = 0; y < Hh; y += 3) {
        cx.beginPath(), cx.moveTo(0, y), cx.lineTo(W, y), cx.stroke();
      }
      // vignettatura morbida ai bordi, dà volume senza disegnare nulla
      const vg = cx.createRadialGradient(W / 2, Hh / 2, Hh * 0.25, W / 2, Hh / 2, Hh * 0.68);
      (vg.addColorStop(0, "rgba(0,0,0,0)"),
        vg.addColorStop(1, "rgba(0,0,0,0.32)"),
        (cx.fillStyle = vg),
        cx.fillRect(0, 0, W, Hh));
      const rugTex = new THREE.CanvasTexture(o),
        rugW = 4,
        rugD = 2.5,
        rugMat = new THREE.MeshStandardMaterial({
          map: rugTex,
          roughness: 0.95,
          metalness: 0,
        }),
        rug = new THREE.Mesh(new THREE.PlaneGeometry(rugW, rugD), rugMat);
      (rug.rotation.x = -Math.PI / 2),
        rug.position.set(0, 0.006, 0.32),
        (rug.receiveShadow = !0),
        deskGroup.add(rug);
    })();

    // piano
    const top = new THREE.Mesh(
      new THREE.BoxGeometry(deskTopW, 0.04, deskTopD),
      woodMat,
    );
    ((top.position.y = deskH), (top.castShadow = !0), deskGroup.add(top));
    // gambe
    [
      [-deskTopW / 2 + 0.05, -deskTopD / 2 + 0.05],
      [deskTopW / 2 - 0.05, -deskTopD / 2 + 0.05],
      [-deskTopW / 2 + 0.05, deskTopD / 2 - 0.05],
      [deskTopW / 2 - 0.05, deskTopD / 2 - 0.05],
    ].forEach(([lx, lz]) => {
      const leg = new THREE.Mesh(
        new THREE.BoxGeometry(0.045, deskH, 0.045),
        metalMat,
      );
      (leg.position.set(lx, deskH / 2, lz), (leg.castShadow = !0), deskGroup.add(leg));
    });
    // lampada da tavolo: base, braccio articolato in due segmenti (non più
    // un'unica asta inclinata), paralume con bordo e un accenno di
    // lampadina visibile sotto — l'unica luce calda ravvicinata
    const lampGroup = new THREE.Group();
    const lampBase = new THREE.Mesh(
        new THREE.CylinderGeometry(0.085, 0.1, 0.022, 24),
        metalMat,
      ),
      lampRiser = new THREE.Mesh(
        new THREE.CylinderGeometry(0.013, 0.015, 0.16, 10),
        metalMat,
      ),
      lampJoint1 = new THREE.Mesh(new THREE.SphereGeometry(0.017, 12, 12), metalMat),
      lampArm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.011, 0.011, 0.24, 10),
        metalMat,
      ),
      lampJoint2 = new THREE.Mesh(new THREE.SphereGeometry(0.015, 12, 12), metalMat),
      lampShadeMat = new THREE.MeshStandardMaterial({
        color: 0xffdca0,
        emissive: 0xcc8a2e,
        emissiveIntensity: 0.9,
        roughness: 0.5,
        side: THREE.DoubleSide,
      }),
      lampShade = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.15, 24, 1, !0), lampShadeMat),
      lampRim = new THREE.Mesh(
        new THREE.TorusGeometry(0.1, 0.006, 8, 24),
        metalMat,
      ),
      lampBulb = new THREE.Mesh(
        new THREE.SphereGeometry(0.018, 10, 10),
        new THREE.MeshStandardMaterial({
          color: 0xfff2d0,
          emissive: 0xffcf7a,
          emissiveIntensity: 1.4,
        }),
      );
    const ARM_ANGLE = 0.85, // radianti dalla verticale
      dx = Math.sin(ARM_ANGLE) * 0.24,
      dy = Math.cos(ARM_ANGLE) * 0.24,
      j1y = 0.022 + 0.16,
      j2x = dx,
      j2y = j1y + dy;
    (lampBase.position.set(0, 0.011, 0),
      lampRiser.position.set(0, 0.022 + 0.08, 0),
      lampJoint1.position.set(0, j1y, 0),
      lampArm.position.set(dx / 2, j1y + dy / 2, 0),
      (lampArm.rotation.z = -ARM_ANGLE),
      lampJoint2.position.set(j2x, j2y, 0),
      lampShade.position.set(j2x, j2y - 0.065, 0),
      lampRim.position.set(j2x, j2y - 0.14, 0),
      (lampRim.rotation.x = Math.PI / 2),
      lampBulb.position.set(j2x, j2y - 0.13, 0),
      [lampBase, lampRiser, lampJoint1, lampArm, lampJoint2, lampShade, lampRim].forEach((m) => {
        (m.castShadow = !0), lampGroup.add(m);
      }),
      lampGroup.add(lampBulb));
    const lampLight = new THREE.PointLight(0xffb066, 1.9, 5, 2);
    (lampLight.position.set(j2x, j2y - 0.12, 0), lampGroup.add(lampLight));
    (lampGroup.position.set(
      deskTopW / 2 - 0.22,
      deskH + 0.02,
      -deskTopD / 2 + 0.16,
    ),
      deskGroup.add(lampGroup));

    // --- Bottiglia di vino ---------------------------------------------
    const glassMat = new THREE.MeshStandardMaterial({
        color: 0x0c1f14,
        roughness: 0.12,
        metalness: 0.15,
      }),
      bottleGroup = new THREE.Group(),
      bottleBody = new THREE.Mesh(
        new THREE.CylinderGeometry(0.038, 0.045, 0.26, 16),
        glassMat,
      ),
      bottleNeck = new THREE.Mesh(
        new THREE.CylinderGeometry(0.014, 0.022, 0.11, 12),
        glassMat,
      ),
      bottleCap = new THREE.Mesh(
        new THREE.CylinderGeometry(0.015, 0.015, 0.018, 12),
        new THREE.MeshStandardMaterial({ color: 0x6e1015, roughness: 0.4, metalness: 0.3 }),
      );
    (bottleBody.position.y = 0.13,
      bottleNeck.position.y = 0.26 + 0.055,
      bottleCap.position.y = 0.26 + 0.11 + 0.009,
      [bottleBody, bottleNeck, bottleCap].forEach((m) => {
        (m.castShadow = !0), bottleGroup.add(m);
      }));
    (bottleGroup.position.set(-0.36, deskH + 0.02, -0.08), deskGroup.add(bottleGroup));

    // --- Lecca lecca rosso, sulla scrivania ------------------------------
    // Disco piatto (non una sfera), dimensioni dimezzate. Prima era per
    // terra e il bastoncino finiva sotto il livello del tappeto, quindi
    // invisibile — bastoncino ora più spesso e tutto rimesso sul tavolo.
    const lolliGroup = new THREE.Group(),
      lolliCandy = new THREE.Mesh(
        new THREE.CylinderGeometry(0.016, 0.016, 0.012, 24),
        new THREE.MeshStandardMaterial({ color: 0xd21b2c, roughness: 0.25, metalness: 0.05 }),
      ),
      lolliStick = new THREE.Mesh(
        new THREE.CylinderGeometry(0.0035, 0.0035, 0.09, 8),
        new THREE.MeshStandardMaterial({ color: 0xf2ead8, roughness: 0.6 }),
      );
    (lolliCandy.position.y = 0.006,
      lolliStick.rotation.z = Math.PI / 2,
      lolliStick.position.set(0.016 + 0.09 / 2 - 0.01, 0.0035, 0),
      lolliCandy.castShadow = !0,
      lolliStick.castShadow = !0,
      lolliGroup.add(lolliCandy),
      lolliGroup.add(lolliStick));
    (lolliGroup.rotation.y = 0.4,
      lolliGroup.position.set(0.28, deskH + 0.02, 0.22),
      deskGroup.add(lolliGroup));

    // --- Il libro: cliccabile, apre i tre testi di Anatomia del Margine -
    // Tre strati veri (copertina, blocco pagine, copertina) invece di due
    // scatole piatte — con bordo pagine visibile e titolo sulla copertina.
    const bookGroup = new THREE.Group(),
      coverW = 0.2,
      coverD = 0.15,
      pageW = 0.186,
      pageD = 0.138,
      botH = 0.012,
      pageH = 0.05,
      topH = 0.012,
      spineMat = new THREE.MeshStandardMaterial({
        map: frameTex,
        color: 0x4a0d12,
        roughness: 0.6,
        metalness: 0,
      }),
      bottomCover = new THREE.Mesh(new THREE.BoxGeometry(coverW, botH, coverD), spineMat);
    // bordo pagine: righe orizzontali sottili, così i lati del blocco
    // pagine leggono come carta impilata e non come un blocco pieno
    const pageSideTex = (function () {
        const c = document.createElement("canvas");
        (c.width = 48), (c.height = 256);
        const px = c.getContext("2d");
        (px.fillStyle = "#efe4c8"), px.fillRect(0, 0, 48, 256);
        for (let y = 0; y < 256; y += 3) {
          px.fillStyle = "rgba(120,100,70," + (0.05 + Math.random() * 0.09) + ")";
          px.fillRect(0, y, 48, 1);
        }
        const t = new THREE.CanvasTexture(c);
        return ((t.wrapS = t.wrapT = THREE.RepeatWrapping), t);
      })(),
      pageSideMat = new THREE.MeshStandardMaterial({ map: pageSideTex, roughness: 0.88 }),
      pageTopMat = new THREE.MeshStandardMaterial({ color: 0xeee3c8, roughness: 0.85 }),
      pageBlock = new THREE.Mesh(new THREE.BoxGeometry(pageW, pageH, pageD), [
        pageSideMat,
        pageSideMat,
        pageTopMat,
        pageTopMat,
        pageSideMat,
        pageSideMat,
      ]);
    // le pagine sono a filo con le copertine sul lato del dorso (-Z) e
    // rientrate sul taglio davanti (+Z) — prima erano centrate su tutti
    // i lati, per questo il libro sembrava un blocco a strati uguali
    // (un "maxibon") invece che un libro rilegato
    pageBlock.position.z = -(coverD - pageD) / 2;
    // copertina superiore: titolo inciso, cornice dorata sottile — l'unica
    // faccia che si vede davvero, guardando la scrivania dall'alto
    const coverTopTex = (function () {
        const c = document.createElement("canvas");
        (c.width = 512), (c.height = 384);
        const px = c.getContext("2d");
        (px.fillStyle = "#4a0d12"), px.fillRect(0, 0, 512, 384);
        for (let i = 0; i < 900; i++) {
          px.fillStyle = "rgba(0,0,0," + (0.03 + Math.random() * 0.05) + ")";
          px.fillRect(Math.random() * 512, Math.random() * 384, 2, 2);
        }
        (px.strokeStyle = "rgba(201,162,74,0.55)"),
          (px.lineWidth = 4),
          px.strokeRect(22, 22, 512 - 44, 384 - 44);
        (px.strokeStyle = "rgba(201,162,74,0.28)"),
          (px.lineWidth = 1.5),
          px.strokeRect(34, 34, 512 - 68, 384 - 68);
        ((px.fillStyle = "rgba(214,182,122,0.88)"),
          (px.textAlign = "center"),
          (px.textBaseline = "middle"),
          (px.font = "italic 300 36px Georgia, serif"),
          px.fillText("ANATOMIA", 256, 168),
          px.fillText("DEL MARGINE", 256, 216));
        const t = new THREE.CanvasTexture(c);
        return ((t.encoding = THREE.sRGBEncoding), t);
      })(),
      topCoverMat = new THREE.MeshStandardMaterial({ map: coverTopTex, roughness: 0.5 }),
      topCover = new THREE.Mesh(new THREE.BoxGeometry(coverW, topH, coverD), [
        spineMat,
        spineMat,
        topCoverMat,
        spineMat,
        spineMat,
        spineMat,
      ]);
    (bottomCover.position.y = botH / 2,
      pageBlock.position.y = botH + pageH / 2,
      topCover.position.y = botH + pageH + topH / 2,
      [bottomCover, pageBlock, topCover].forEach((m) => {
        (m.castShadow = !0), bookGroup.add(m);
      }));
    // dorso piatto: un'unica placca che copre l'intera altezza e
    // larghezza del bordo rilegato, a filo con le due copertine.
    const totalH = botH + pageH + topH,
      spine = new THREE.Mesh(new THREE.BoxGeometry(coverW, totalH, 0.01), spineMat);
    (spine.position.set(0, totalH / 2, -coverD / 2 - 0.005),
      (spine.castShadow = !0),
      bookGroup.add(spine));
    // Tutto il libro è cliccabile (prima solo la copertina superiore):
    // bersaglio più grande, soprattutto al tocco.
    [bottomCover, pageBlock, topCover, spine].forEach((m) => {
      ((m.userData = { type: "book" }), Jt.push(m));
    });
    // --- Bagliore quando il cursore passa sopra --------------------------
    // Tre strati che si accendono insieme: una luce calda emessa dal libro
    // stesso, un alone morbido sul piano della scrivania e una piccola
    // luce puntiforme che illumina gli oggetti vicini.
    const glowMats = [spineMat, topCoverMat, pageSideMat, pageTopMat];
    glowMats.forEach((mm) => {
      (mm.emissive.setHex(0xffa24a), (mm.emissiveIntensity = 0));
    });
    const haloTex = (function () {
        const c = document.createElement("canvas");
        c.width = c.height = 128;
        const hx = c.getContext("2d"),
          hg = hx.createRadialGradient(64, 64, 0, 64, 64, 64);
        (hg.addColorStop(0, "rgba(255,190,110,0.9)"),
          hg.addColorStop(0.45, "rgba(255,160,80,0.35)"),
          hg.addColorStop(1, "rgba(255,150,70,0)"),
          (hx.fillStyle = hg),
          hx.fillRect(0, 0, 128, 128));
        return new THREE.CanvasTexture(c);
      })(),
      haloMat = new THREE.MeshBasicMaterial({
        map: haloTex,
        transparent: !0,
        opacity: 0,
        depthWrite: !1,
        blending: THREE.AdditiveBlending,
        toneMapped: !1,
      }),
      halo = new THREE.Mesh(new THREE.PlaneGeometry(0.46, 0.4), haloMat),
      bookLight = new THREE.PointLight(0xffb066, 0, 1.1, 2);
    ((halo.rotation.x = -Math.PI / 2),
      (halo.position.y = 0.002),
      (halo.renderOrder = 2),
      bookGroup.add(halo),
      bookLight.position.set(0, 0.16, 0.02),
      bookGroup.add(bookLight));
    bookHover = {
      meshes: [bottomCover, pageBlock, topCover, spine],
      target: 0,
      level: 0,
    };
    ot.push({
      update: (elapsed, dt) => {
        const bh = bookHover;
        bh.level += (bh.target - bh.level) * Math.min(1, 7 * dt);
        // leggerissimo respiro mentre è acceso, per sembrare luce viva
        const lv = bh.level * (0.92 + 0.08 * Math.sin(elapsed * 3));
        glowMats.forEach((mm) => (mm.emissiveIntensity = 0.24 * lv));
        ((haloMat.opacity = 0.85 * lv), (bookLight.intensity = 1.1 * lv));
      },
    });
    (bookGroup.position.set(0.04, deskH + 0.02, 0.14),
      (bookGroup.rotation.y = Math.PI / 2 - 0.08), // dorso a sinistra, vista dalla sedia
      deskGroup.add(bookGroup));

    // sedia da lavoro: seduta, schienale, base a colonna — geometria
    // volutamente semplice, coerente con l'essenzialità della stanza
    const chairGroup = new THREE.Group(),
      chairMat = new THREE.MeshStandardMaterial({
        color: 0x18181a,
        roughness: 0.7,
        metalness: 0.1,
      }),
      seat = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.05, 0.4), chairMat),
      back = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.4, 0.05), chairMat),
      column = new THREE.Mesh(
        new THREE.CylinderGeometry(0.03, 0.03, 0.42, 10),
        metalMat,
      ),
      chairBase = new THREE.Mesh(
        new THREE.CylinderGeometry(0.22, 0.22, 0.03, 20),
        metalMat,
      );
    (seat.position.y = 0.44,
      back.position.set(0, 0.44 + 0.22, -0.18),
      (back.rotation.x = -0.12),
      column.position.y = 0.23,
      chairBase.position.y = 0.02,
      [seat, back, column, chairBase].forEach((m) => {
        (m.castShadow = !0), chairGroup.add(m);
      }));
    // la sedia sta di fronte alla scrivania, tra questa e l'ingresso,
    // rivolta verso la finestra
    (chairGroup.position.set(0, 0, deskTopD / 2 + 0.55),
      (chairGroup.rotation.y = Math.PI),
      deskGroup.add(chairGroup));

    deskGroup.position.set(0, 0, 0);
    v.add(deskGroup);
    pt.push({ x: 0, z: 0 }, { x: 0, z: 0.65 });
  })();

  // --- Due lampade da terra negli angoli in fondo -------------------------
  // La finestra illumina il fronte della stanza, ma il fondo (vicino
  // all'ingresso) restava troppo scuro. Due lampade da terra, luce calda,
  // colmano il vuoto senza rompere l'atmosfera fredda del resto.
  (function buildFloorLamps() {
    const lampMat = new THREE.MeshStandardMaterial({
        color: 0x111113,
        roughness: 0.5,
        metalness: 0.4,
      }),
      shadeMat = new THREE.MeshStandardMaterial({
        color: 0xf0dcb0,
        emissive: 0xb9782c,
        emissiveIntensity: 0.8,
        roughness: 0.55,
        side: THREE.DoubleSide,
      });
    [
      [-R / 2 + 1.1, H / 2 - 1.1],
      [R / 2 - 1.1, H / 2 - 1.1],
    ].forEach(([lx, lz]) => {
      const group = new THREE.Group(),
        base = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.19, 0.03, 24), lampMat),
        pole = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 1.32, 10), lampMat),
        shade = new THREE.Mesh(
          new THREE.ConeGeometry(0.22, 0.3, 24, 1, !0),
          shadeMat,
        );
      (base.position.y = 0.016,
        pole.position.y = 0.68,
        shade.position.y = 1.36,
        [base, pole, shade].forEach((m) => {
          (m.castShadow = !0), group.add(m);
        }));
      const light = new THREE.PointLight(0xffb066, 2.4, 7.5, 2);
      (light.position.y = 1.3, group.add(light));
      (group.position.set(lx, 0, lz), v.add(group));
    });
  })();

  // --- Nessun cartello di uscita in scena --------------------------------
  // La stanza è volutamente priva di uscite visibili. Si esce solo tramite
  // il pulsante "Exit" nell'HUD (livello interfaccia, non architettura) —
  // vedi il listener su #exit-btn più sotto.

  // --- Camera, drag-to-look, raycasting per aprire il pannello opera -------
  const se = new THREE.Vector3(e.spawn.x, e.eyeHeight, e.spawn.z);
  let re = 0,
    ce = 0;
  const le = document.getElementById("panel"),
    de = document.getElementById("exit-confirm");
  let he = !1,
    Ee = null,
    pe = 0,
    me = 0,
    ue = 0,
    Me = 0,
    fe = 0;
  const we = e.lookSensitivity;
  (S.addEventListener("pointerdown", (t) => {
    !T ||
      le.classList.contains("open") ||
      de.classList.contains("show") ||
      document.getElementById("book-panel").classList.contains("open") ||
      ((he = !0),
      (Ee = t.pointerId),
      (pe = ue = t.clientX),
      (me = Me = t.clientY),
      (fe = performance.now()),
      S.setPointerCapture(t.pointerId));
  }),
    S.addEventListener("pointermove", (t) => {
      if (!he || t.pointerId !== Ee) return;
      const e = t.clientX - pe,
        n = t.clientY - me;
      // Al tocco si "trascina il mondo" (come Street View e Google Maps):
      // dito a destra = sguardo a sinistra. Col mouse resta come prima.
      const dir = "touch" === t.pointerType ? -1 : 1;
      ((pe = t.clientX),
        (me = t.clientY),
        (re -= dir * e * we),
        (ce -= dir * n * we),
        (ce = Math.max(-1.3, Math.min(1.3, ce))));
    }),
    S.addEventListener("pointerup", (t) => {
      if (t.pointerId !== Ee) return;
      he = !1;
      const e = Math.hypot(t.clientX - ue, t.clientY - Me),
        n = performance.now() - fe;
      e < 8 &&
        n < 400 &&
        (function (t, e) {
          const n = P.domElement.getBoundingClientRect(),
            a = ((t - n.left) / n.width) * 2 - 1,
            o = (-(e - n.top) / n.height) * 2 + 1;
          ge.setFromCamera({ x: a, y: o }, I);
          const i = ge.intersectObjects(Jt);
          if (!i.length) return;
          const s = i[0].object;
          "artwork" === s.userData.type
            ? (function (t) {
                const e = l[t];
                ((document.getElementById("p-num").textContent =
                  String(t + 1).padStart(2, "0") +
                  " / " +
                  String(l.length).padStart(2, "0")),
                  (document.getElementById("p-title").textContent = e.title),
                  (document.getElementById("p-meta").textContent = e.meta),
                  (document.getElementById("p-price").textContent = e.price),
                  (document.getElementById("p-view").href = e.url),
                  (ke = e.variantId || null),
                  (Be = e.url));
                // Foto dell'opera nel pannello.
                const img = document.getElementById("p-img");
                img &&
                  (e.imageUrl
                    ? ((img.src = e.imageUrl),
                      (img.alt = e.title),
                      (img.style.display = "block"))
                    : (img.style.display = "none"));
                const pc = document.getElementById("p-cart");
                pc && (pc.hidden = !0);
                // Disponibilità letta da Shopify: se esaurita, niente carrello.
                const avail = !1 !== e.available,
                  n = document.getElementById("p-add-cart");
                ((n.disabled = !avail),
                  (n.textContent = avail ? "Add to cart" : "Sold out"),
                  (le.scrollTop = 0),
                  le.classList.add("open"),
                  setJoystick(!1),
                  g());
              })(s.userData.index)
            : "book" === s.userData.type
              ? openBook(0)
              : "exit" === s.userData.type && openExit();
        })(t.clientX, t.clientY);
    }));
  const ge = new THREE.Raycaster();

  // --- Hover sul libro (solo mouse) --------------------------------------
  // Mentre non si trascina e nessun pannello è aperto, un raggio dal
  // cursore controlla se si è sopra al libro: accende il bagliore e mostra
  // la manina del link.
  const hoverRay = new THREE.Raycaster();
  function setBookHover(on) {
    bookHover && (bookHover.target = on ? 1 : 0);
    S.style.cursor = on ? "pointer" : "";
  }
  (S.addEventListener("pointermove", (t) => {
    if ("touch" === t.pointerType || !bookHover) return;
    if (
      !T ||
      he ||
      le.classList.contains("open") ||
      de.classList.contains("show") ||
      document.getElementById("book-panel").classList.contains("open")
    )
      return void setBookHover(!1);
    const r = P.domElement.getBoundingClientRect();
    (hoverRay.setFromCamera(
      {
        x: ((t.clientX - r.left) / r.width) * 2 - 1,
        y: (-(t.clientY - r.top) / r.height) * 2 + 1,
      },
      I,
    ),
      setBookHover(hoverRay.intersectObjects(bookHover.meshes).length > 0));
  }),
    S.addEventListener("pointerleave", () => setBookHover(!1)));

  // --- Il libro sulla scrivania: i tre testi di Anatomia del Margine -----
  const bookPanelEl = document.getElementById("book-panel");
  function openBook(i) {
    const entry = libro[i];
    (document.querySelectorAll(".book-tab").forEach((btn, bi) => {
      btn.classList.toggle("active", bi === i);
    }),
      (document.getElementById("book-title").textContent = entry.title),
      (document.getElementById("book-text").textContent = entry.text),
      (document.getElementById("book-text").scrollTop = 0),
      bookPanelEl.classList.add("open"),
      setJoystick(!1),
      setBookHover(!1),
      g());
  }
  (document.querySelectorAll(".book-tab").forEach((btn) => {
    btn.addEventListener("click", () => openBook(Number(btn.dataset.i)));
  }),
    document.getElementById("book-close-x").addEventListener("click", () => {
      (bookPanelEl.classList.remove("open"), setJoystick(T));
    }));
  // Apre la conferma di uscita (dal pulsante Exit nell'HUD).
  function openExit() {
    (de.classList.add("show"), setJoystick(!1), g());
  }

  // --- Joystick virtuale (mobile) -------------------------------------------
  const Te = document.getElementById("joystick-zone"),
    ye = document.getElementById("joystick-knob");
  let Re = !1,
    He = null,
    xe = { x: 0, y: 0 };
  function Se(t) {
    t.pointerId === He &&
      ((Re = !1),
      (xe = { x: 0, y: 0 }),
      (ye.style.transform = "translate(0,0)"));
  }
  function ve(t) {
    const e = Te.getBoundingClientRect(),
      n = e.left + e.width / 2,
      a = e.top + e.height / 2;
    let o = t.clientX - n,
      i = t.clientY - a;
    const s = Math.min(Math.hypot(o, i), 40),
      r = Math.atan2(i, o);
    ((o = Math.cos(r) * s),
      (i = Math.sin(r) * s),
      (ye.style.transform = `translate(${o}px, ${i}px)`),
      (xe.x = o / 40),
      (xe.y = i / 40));
  }
  (Te.addEventListener("pointerdown", (t) => {
    ((Re = !0), (He = t.pointerId), Te.setPointerCapture(t.pointerId), ve(t));
  }),
    Te.addEventListener("pointermove", (t) => {
      Re && t.pointerId === He && ve(t);
    }),
    Te.addEventListener("pointerup", Se),
    Te.addEventListener("pointercancel", Se));

  // --- Tastiera WASD ----------------------------------------------------
  const Ie = { forward: !1, back: !1, left: !1, right: !1 };
  (document.addEventListener("keydown", (t) => {
    // Niente movimento prima di entrare o mentre si scrive nel campo email.
    if (!T || (t.target && /^(INPUT|TEXTAREA)$/.test(t.target.tagName))) return;
    (("KeyW" !== t.code && "ArrowUp" !== t.code) || (Ie.forward = !0),
      ("KeyS" !== t.code && "ArrowDown" !== t.code) || (Ie.back = !0),
      ("KeyA" !== t.code && "ArrowLeft" !== t.code) || (Ie.left = !0),
      ("KeyD" !== t.code && "ArrowRight" !== t.code) || (Ie.right = !0));
  }),
    document.addEventListener("keyup", (t) => {
      (("KeyW" !== t.code && "ArrowUp" !== t.code) || (Ie.forward = !1),
        ("KeyS" !== t.code && "ArrowDown" !== t.code) || (Ie.back = !1),
        ("KeyA" !== t.code && "ArrowLeft" !== t.code) || (Ie.left = !1),
        ("KeyD" !== t.code && "ArrowRight" !== t.code) || (Ie.right = !1));
    }));

  // --- Movimento + evitamento ostacoli (usa `pt`, vuoto di default) -----
  const Pe = new THREE.Clock(),
    be = new THREE.Vector3(),
    Ce = new THREE.Vector3();
  let Ge = 0,
    camYaw = 0,
    camPitch = 0,
    camBreath = 0;
  function ze() {
    const t = Math.min(Pe.getDelta(), 0.1);
    let n = 0,
      a = 0;
    (Ie.forward && (a -= 1),
      Ie.back && (a += 1),
      Ie.left && (n -= 1),
      Ie.right && (n += 1),
      Re && ((n += xe.x), (a += xe.y)));
    const o = Math.hypot(n, a);
    if (o > 0.001) {
      (o > 1 && ((n /= o), (a /= o)),
        be.set(-Math.sin(re), 0, -Math.cos(re)),
        Ce.set(Math.cos(re), 0, -Math.sin(re)));
      const i = e.moveSpeed * t;
      (se.addScaledVector(be, -a * i),
        se.addScaledVector(Ce, n * i),
        (Ge -= t),
        Ge <= 0 &&
          (w(0.06, 0.022 + 0.012 * Math.random(), 380 + 220 * Math.random()),
          (Ge = 0.42)));
    } else Ge = 0;
    const i = e.wallMargin;
    ((se.x = Math.max(-R / 2 + i, Math.min(R / 2 - i, se.x))),
      (se.z = Math.max(-H / 2 + i, Math.min(H / 2 - i, se.z))));
    (pt.forEach((t) => {
      const e = se.x - t.x,
        n = se.z - t.z,
        a = Math.hypot(e, n),
        o = OBSTACLE_RADIUS + 0.35;
      a < o &&
        a > 1e-4 &&
        ((se.x = t.x + (e / a) * o), (se.z = t.z + (n / a) * o));
    }),
      I.position.copy(se));
    // Sguardo con inerzia: la camera insegue l'angolo voluto (re/ce) invece
    // di saltarci sopra. Indipendente dal frame rate.
    const kLook = 1 - Math.exp(-CINE.lookSmoothing * t);
    ((camYaw += (re - camYaw) * kLook),
      (camPitch += (ce - camPitch) * kLook),
      (I.rotation.y = camYaw),
      (I.rotation.x = camPitch));
    // Respiro: oscillazione verticale minima, solo da fermi.
    ((camBreath += t),
      o > 0.001 || (I.position.y += CINE.breath * Math.sin(1.1 * camBreath)));
  }

  // --- Pannello opera + aggiungi al carrello -----------------------------
  let ke = null,
    Be = "#";
  function Le() {
    (le.classList.remove("open"), setJoystick(T), g());
  }
  (document.getElementById("p-add-cart").addEventListener("click", (t) =>
    (async function (t, e) {
      if (!t)
        return (
          console.warn(
            "variantId non configurato per questa opera — apro la pagina prodotto.",
          ),
          void window.open(Be, "_blank")
        );
      const n = e.textContent;
      ((e.disabled = !0), (e.textContent = "Adding…"));
      let ok = !1;
      try {
        const n = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: Number(t), quantity: 1 }] }),
        });
        if (!n.ok) throw new Error("status " + n.status);
        ((ok = !0), (e.textContent = "Added ✓"));
        const pc = document.getElementById("p-cart");
        pc && (pc.hidden = !1);
      } catch (t) {
        (console.error("Errore aggiunta al carrello:", t),
          (e.textContent = "Error — try again"));
      } finally {
        setTimeout(() => {
          ((e.disabled = !1), (e.textContent = ok ? "Add another" : n));
        }, 1800);
      }
    })(ke, t.currentTarget),
  ),
    document.getElementById("p-close").addEventListener("click", Le),
    document.getElementById("panel-close-x").addEventListener("click", Le),
    document.getElementById("exit-stay").addEventListener("click", () => {
      (de.classList.remove("show"), setJoystick(T), g());
    }),
    document.getElementById("exit-go").addEventListener("click", () => {
      (g(), de.classList.remove("show"), (window.location.href = c));
    }),
    document.getElementById("exit-btn").addEventListener("click", openExit),
    document.getElementById("mute-btn").addEventListener("click", function () {
      ((u = !u), p && (p.gain.value = u ? 0 : o.masterVolume));
      const t = document.getElementById("mute-btn");
      (t && (t.textContent = u ? "🔇" : "🔊"), u || g());
    }),
    document.addEventListener("keydown", (t) => {
      "Escape" === t.code &&
        (le.classList.remove("open"),
        de.classList.remove("show"),
        bookPanelEl.classList.remove("open"),
        setJoystick(T));
    }),
    window.addEventListener("pageshow", (t) => {
      t.persisted &&
        (le.classList.remove("open"),
        de.classList.remove("show"),
        bookPanelEl.classList.remove("open"),
        setJoystick(T));
    }),
    window.addEventListener("resize", () => {
      ((I.aspect = window.innerWidth / window.innerHeight),
        I.updateProjectionMatrix(),
        P.setSize(window.innerWidth, window.innerHeight),
        composer && composer.setSize(window.innerWidth, window.innerHeight));
    }));

  // --- Ascoltatore audio 3D = posizione/orientamento della camera --------
  function updateAudioListener() {
    if (!E) return;
    const t = I.position,
      e = E.listener;
    e.positionX
      ? ((e.positionX.value = t.x),
        (e.positionY.value = t.y),
        (e.positionZ.value = t.z))
      : e.setPosition && e.setPosition(t.x, t.y, t.z);
    const n = -Math.sin(re) * Math.cos(ce),
      a = Math.sin(ce),
      o = -Math.cos(re) * Math.cos(ce);
    e.forwardX
      ? ((e.forwardX.value = n),
        (e.forwardY.value = a),
        (e.forwardZ.value = o),
        (e.upX.value = 0),
        (e.upY.value = 1),
        (e.upZ.value = 0))
      : e.setOrientation && e.setOrientation(n, a, o, 0, 1, 0);
  }

  // --- Loop di animazione --------------------------------------------------
  const De = new THREE.Clock();
  let Ae = 0;
  function animate() {
    requestAnimationFrame(animate);
    const dt = Math.min(De.getDelta(), 0.1);
    Ae += dt;
    ze();
    updateAudioListener();
    ot.forEach((n) => n.update(Ae, dt));
    composer ? composer.render() : P.render(v, I);
  }
  animate();
})();
